import __ from './translations/translate';
import { getCurrentInstance } from 'vue';
import agent from './agent';
import { POSITION, useToast } from 'vue-toastification';
import ToastificationContent from './components/toastification/ToastificationContent';
import nl from './translations/datatable_dutch';
import de from './translations/datatable_german';
import cs from './translations/datatable_czech';
import pl from './translations/datatable_polish';
import ReconnectingWebSocket from 'reconnectingwebsocket';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import Swal from 'sweetalert2';
import { createPopper } from '@popperjs/core';
import { wdDate, wdDateTime, wdTime, wdLongDate } from './utils/dates.js';

const _ = require('underscore');
const clone = require('lodash.clone');
const cloneDeep = require('lodash.clonedeep');
const IBAN = require('iban');

const hasOwn = (object, property) => Object.prototype.hasOwnProperty.call(Object(object), property);
const debounce_mixin = {
    data() {
        return {
            debounceTimeoutFetch: null,
        };
    },
    methods: {
        debounceCallback(callback, timeout = 500) {
            /*
             * Won't work with return new Promise() callbacks
             *
             * Suitable when need to execute function in its own scope
             * usually for v-select @input, @search and etc functions
             * */
            if (this.debounceTimeoutFetch) {
                clearTimeout(this.debounceTimeoutFetch);
            }
            this.debounceTimeoutFetch = setTimeout(() => {
                callback();
            }, timeout);
        },
        debounceChecker(optionObject, searchObject, callback) {
            /**
             * Takes option object and search param of fetch function and checks if callback can be triggered
             * IMPORTANT to have @search event on v-select for search and @click for first-time-click fetch
             */
            if (
                (Array.isArray(optionObject) && !optionObject.length && typeof searchObject === 'object') ||
                typeof searchObject !== 'object'
            ) {
                if (typeof searchObject === 'object') {
                    searchObject = '';
                }
                callback(searchObject);
            }
        },
    },
};

const currency_mixin = {
    methods: {
        currencyToString(amount) {
            /**
             * @requires default_currency(example:'EUR')
             * @requires default_currency_locale(example:'nl-NL')
             */
            return new Intl.NumberFormat(default_currency_locale, {
                style: 'currency',
                currency: default_currency,
                maximumFractionDigits: 5,
            }).format(amount);
        },
        decimalToString(amount) {
            /**
             * @requires default_currency(example:'EUR')
             * @requires default_currency_locale(example:'nl-NL')
             */
            return new Intl.NumberFormat(default_currency_locale, {
                maximumFractionDigits: 5,
            }).format(amount);
        },
        roundedAmountToString(amount) {
            /**
             * @requires default_currency_locale(example:'nl-NL')
             */
            return new Intl.NumberFormat(default_currency_locale, {
                maximumFractionDigits: 0,
            }).format(amount);
        },
        /**
         * Cleans price input (swaps commas for dots, removes thousands separators)
         * only for price tier
         */
        formatPrice(priceItem) {
            let value = priceItem.price;
            if (value === null || value === undefined || value === '') {
                return;
            }

            value = String(value);
            if (value.indexOf(',') > -1) {
                value = value.replace(/,/g, '.'); // Swap comma to dot
            }
            value = value.replace(/[^0-9.-]/g, '');

            const parts = value.split('.');
            let decimalCount = 4;
            if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
            }
            if (parts.length === 2) {
                decimalCount = parts[1].length;
            }

            const numericValue = parseFloat(value).toFixed(decimalCount);
            if (numericValue !== 'NaN' && numericValue !== '' && numericValue !== null && numericValue !== undefined) {
                priceItem.price = numericValue;
            } else {
                priceItem.price = 0;
            }
        },
    },
};

const utils = {
    mixins: [currency_mixin, debounce_mixin],
    data() {
        return {
            disabledFields: [],
            filelist: [],
            currentTimeZone: time_zone,
            filterKeypressHandler: null,
        };
    },
    methods: {
        getCountryCode(country) {
            if (!country) {
                return '';
            }
            if (typeof country === 'string') {
                return country.toUpperCase();
            }
            return String(country.code || country.country_code || country.iso_3166_1_alpha_2 || '').toUpperCase();
        },
        isForeignRelation(relation) {
            const relationCountry = this.getCountryCode(
                relation && (relation.country || (relation.billing_address && relation.billing_address.country))
            );
            const instanceCountry = typeof current_country !== 'undefined' ? String(current_country).toUpperCase() : '';
            return !!relationCountry && !!instanceCountry && relationCountry !== instanceCountry;
        },
        analysisItemBenchmarkCheckDeviation(analysisItem, result) {
            if (!result) {
                return false;
            }
            result = parseFloat(result);
            let benchmark = parseFloat(analysisItem.benchmark);
            if (analysisItem.operator) {
                if (analysisItem.operator.id === 1) {
                    return !(result > benchmark);
                } else if (analysisItem.operator.id === 2) {
                    return !(result === benchmark);
                } else if (analysisItem.operator.id === 3) {
                    return !(result < benchmark);
                }
            }
            return true;
        },
        yesOrNoText(param = false) {
            return param ? this.$__('Yes') : this.$__('No');
        },
        uniqueArray(array) {
            if (Array.isArray(array)) {
                return array.filter((value, index, _self) => index === _self.findIndex(t => t.id === value.id));
            }
            return undefined;
        },
        generateUniqueIdent() {
            return Date.now().toString(36) + Math.random().toString(36).substr(2);
        },
        readableRecurrence: function (recurrence) {
            const types = {
                1: __('days'),
                2: __('weeks'),
                3: __('months'),
                4: __('years'),
            };

            const singular = {
                1: __('day'),
                2: __('week'),
                3: __('month'),
                4: __('year'),
            };

            let part_1 = __('every');
            let part_2 = recurrence['multiplier'] > 1 ? recurrence['multiplier'] : '';
            let part_3 = recurrence['multiplier'] > 1 ? ' ' + types[recurrence['type']] : singular[recurrence['type']];

            return `<span style="white-space:nowrap;">${part_1} ${part_2}${part_3}</span>`;
        },
        readableRecurrenceText: function (recurrence) {
            const types = {
                1: __('days'),
                2: __('weeks'),
                3: __('months'),
                4: __('years'),
            };

            const singular = {
                1: __('day'),
                2: __('week'),
                3: __('month'),
                4: __('year'),
            };

            if (!recurrence) {
                return '';
            }

            const multiplier = recurrence['multiplier'] > 1 ? recurrence['multiplier'] : '';
            const typeLabel =
                recurrence['multiplier'] > 1 ? ' ' + types[recurrence['type']] : singular[recurrence['type']];

            return `${__('every')} ${multiplier}${typeLabel}`.trim();
        },
        isNumeric: function (n) {
            return !isNaN(parseFloat(n)) && isFinite(n);
        },
        formattedNumber(x, add_colors = false, trend = null) {
            try {
                x = parseInt(x);
                let color = '';
                if (x === 0) {
                    return 0;
                }
                if (add_colors) {
                    if (x > 0) {
                        color = 'green';
                    } else {
                        color = 'red';
                    }
                }
                let retVal = x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                if (add_colors || trend) {
                    let trendVal = '';
                    if (trend && trend > 0) {
                        trendVal = '&uarr;';
                    } else if (trend && trend < 0) {
                        trendVal = '&darr;';
                    }
                    return `<span style="color:${color}">${retVal}&nbsp;<span style="font-size:1.3rem; color:${trend && trend > 0 ? 'green' : 'red'}">${trendVal}</span></span>`;
                }
                return retVal;
            } catch (ex) {
                return '-';
            }
        },
        toPrice(v) {
            if (!v && v !== 0) {
                return '';
            }
            if (isNaN(v)) {
                return '';
            }
            return this.currencyToString(v);
        },
        toPriceDecimals: function (v, decimals = 2, euro_sign = false) {
            if (!v && v !== 0) {
                return '';
            }
            if (isNaN(v)) {
                return '';
            }
            let price = parseFloat(v).toFixed(decimals);
            if (decimals === 5 && price.charAt(price.length - 1) == '0') {
                decimals = 4;
                price = parseFloat(v).toFixed(decimals);
            }
            if (decimals === 4 && price.charAt(price.length - 1) == '0') {
                decimals = 3;
                price = parseFloat(v).toFixed(decimals);
            }
            if (decimals === 3 && price.charAt(price.length - 1) == '0') {
                decimals = 2;
                price = parseFloat(v).toFixed(decimals);
            }
            if (euro_sign) {
                return '€ ' + price;
            }
            return price;
        },
        kgToTons(kg, decimals = 3) {
            // The API/DB only ever deals in whole kg; tons are display-only, so
            // trim trailing zeros rather than always showing 3 decimals.
            if (kg === null || kg === undefined || kg === '' || isNaN(kg)) {
                return '';
            }
            let tons = (parseFloat(kg) / 1000).toFixed(decimals);
            if (tons.indexOf('.') !== -1) {
                tons = tons.replace(/0+$/, '').replace(/\.$/, '');
            }
            return tons;
        },
        tonsToKg(tons) {
            if (tons === null || tons === undefined || tons === '' || isNaN(tons)) {
                return null;
            }
            return Math.round(parseFloat(tons) * 1000);
        },
        isValidDate: function (d) {
            return d instanceof Date && !isNaN(d);
        },
        isValidTime: function (t) {
            return t.length === 5 && t.split(':').length === 2;
        },
        isRequired(field) {
            return this.requiredFields && this.requiredFields.indexOf(field) !== -1;
        },
        isDisabled(field) {
            return this.disabledFields.indexOf(field) !== -1;
        },
        notEmptyObject(obj) {
            return obj !== null && obj ? Object.keys(obj).length > 0 : false;
        },
        emptyObject(obj) {
            return obj !== null && obj ? Object.keys(obj).length === 0 : false;
        },
        htmlToPlainText(value) {
            if (value === null || typeof value === 'undefined') {
                return '';
            }

            let text = String(value)
                .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
                .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
                .replace(/<\/(p|div|br|li|tr|h[1-6])\s*>/gi, '\n')
                .replace(/<[^>]+>/g, '');

            if (typeof document !== 'undefined') {
                const textarea = document.createElement('textarea');
                textarea.innerHTML = text;
                text = textarea.value;
            }

            return text.trim();
        },
        sanitizeUppercase(val) {
            if (typeof val != 'undefined' && val != null) {
                return val.toUpperCase().trim();
            }
        },
        sanitizeInitials(val) {
            if (typeof val != 'undefined' && val != null) {
                return this.sanitizeUppercase(val.replace(/[^.a-zA-Z]+/g, ''));
            }
        },
        sanitizeInteger(val) {
            if (typeof val != 'undefined' && val != null) {
                return val.toString().replace(/[^0-9]+/g, '');
            }
        },
        sanitizeEuralCode(val) {
            return val.replace(/[^0-9\.]/g, '');
        },
        sanitizePrice(val) {
            if (typeof val != 'undefined' && val != null) {
                val = val.replace(/[^.0-9]+/g, '');
                val = val.toString().split('.');
                if (val.length === 1) {
                    return val[0];
                }
                if (val.length >= 2) {
                    return val[0] + '.' + val[1];
                }
            }
        },
        sanitizePhoneNumber(val) {
            if (typeof val != 'undefined' && val != null) {
                val = val.replace(/[^0-9]+/g, '');
                if (val.length > 0) {
                    val = '+' + val;
                }
                return val;
            }
        },
        sanitizeEmail(val) {
            if (typeof val != 'undefined' && val != null) {
                val = val.toLowerCase();
                return val.replace(/[^-@_.a-z0-9]+/g, '');
            }
        },
        isValidEmail(val) {
            return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,16}$/g.test(val);
        },
        isValidWasteNumber(val) {
            return /^.{12,12}$/.test(val);
        },
        sanitizeWebsite(val) {
            if (typeof val != 'undefined' && val != null) {
                val = val.toLowerCase();
                return val.replace(/[^-.a-z0-9]+/g, '');
            }
            return '';
        },
        isValidWebsite(val) {
            return /^(?:http(s)?:\/\/)?(www\.)?(?:[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/.test(val);
        },
        isValidIBAN(val) {
            return IBAN.isValid(val);
        },
        // The methods below all delegate to assets/js/utils/dates.js, the
        // one place a date becomes a country-formatted string for a person
        // to read (issue GH-2008 "Project-wide date & time formatting") -
        // CURRENT_COUNTRY, not the active UI language, decides the output,
        // so no call site below needed to change.
        formatDate(date) {
            if (!date) {
                return null;
            }
            return wdDate(date);
        },
        formatDateInt(date, format = 'YYYY-MM-DD') {
            if (!date) {
                return '';
            }
            const m = moment(date, format);
            if (!m.isValid()) {
                return '';
            }
            return wdLongDate(m, { includeWeekday: true });
        },
        formatDateIntNoWeekDay(date, format = 'YYYY-MM-DD') {
            if (!date) {
                return '';
            }
            const m = moment(date, format);
            if (!m.isValid()) {
                return '';
            }
            return wdLongDate(m);
        },
        formatDateShortInt(date, format = 'YYYY-MM-DD') {
            if (!date) {
                return '';
            }
            const m = moment(date, format);
            return wdDate(m);
        },
        formatDateTimeInt(datetime) {
            if (!datetime) {
                return '';
            }
            if (moment.isMoment(datetime)) {
                return wdDateTime(datetime);
            }
            if (datetime instanceof Date || typeof datetime === 'number') {
                return wdDateTime(moment(datetime));
            }

            // Locale is still needed here to *parse* an incoming string
            // that spells out a weekday/month name in the user's language
            // (the 'dddd D MMMM YYYY' formats below) - the *output*,
            // rendered via wdDateTime above/below, is country-driven.
            const langCode =
                this.user && this.user.lang && this.user.lang.code
                    ? this.user.lang.code
                    : this.lang && this.lang.code
                      ? this.lang.code
                      : 'en';
            const momentLocale = langCode === 'nl-be' ? 'nl' : langCode;
            const supportedFormats = [
                moment.ISO_8601,
                'YYYY-MM-DDTHH:mm:ss',
                'YYYY-MM-DDTHH:mm',
                'YYYY-MM-DD HH:mm:ss',
                'YYYY-MM-DD HH:mm',
                'YYYY-MM-DD',
                'DD-MM-YYYY HH:mm:ss',
                'DD-MM-YYYY HH:mm',
                'DD-MM-YYYY',
                'dddd D MMMM YYYY HH:mm',
                'dddd DD MMMM YYYY HH:mm',
                'dddd D MMMM YYYY',
                'dddd DD MMMM YYYY',
            ];
            const m = moment(String(datetime), supportedFormats, momentLocale, true);

            if (!m.isValid()) {
                return String(datetime);
            }

            return wdDateTime(m);
        },
        datatableFormatDateTime(datetime, showTime = true) {
            if (!datetime) {
                return '-';
            }
            let splitted = datetime.split('T');
            let time = null;
            if (splitted.length > 1) {
                time = splitted[1];
            }
            let date = splitted[0];
            let d = moment(date);
            if (time && showTime) {
                d = moment(date + ' ' + time);
            }
            if (!d.isValid()) {
                throw 'invalid date';
            }
            return `${wdDate(d)} <small class="text-nowrap me-1">${showTime ? '- ' + wdTime(d) : ''}</small>`;
        },
        datatableGetDayName(date) {
            let d = moment(date).toDate();
            return d.toLocaleDateString(this.user.lang.code, { weekday: 'long' });
        },
        datatableFormatPriority(priority) {
            let self = this;
            if (priority == 1) {
                return `<div class="todo-item-action">
                            <div class="badge-wrapper me-1">
                                <span class="badge text-capitalize badge-light-success badge-pill">${self.$__('low')}</span>
                            </div>
                       </div>`;
            }
            if (priority == 2) {
                return `<div class="todo-item-action">
                            <div class="badge-wrapper me-1">
                                <span class="badge text-capitalize badge-light-warning badge-pill">${self.$__('medium')}</span>
                            </div>
                       </div>`;
            }
            if (priority == 3) {
                return `<div class="todo-item-action">
                            <div class="badge-wrapper me-1">
                                <span class="badge text-capitalize badge-light-danger badge-pill">${self.$__('high')}</span>
                            </div>
                       </div>`;
            }
        },
        isRecurringTemplateRelatedModel(modelName) {
            const normalizedModelName = (modelName || '').toString().toLowerCase();

            return (
                normalizedModelName === 'collection_orders.collectionordertemplate' ||
                normalizedModelName === 'outsourced_orders.outsourcedordertemplate'
            );
        },
        getRecurringTemplateHref(modelName, templateId) {
            if (!templateId) {
                return '#';
            }

            const normalizedModelName = (modelName || '').toString().toLowerCase();
            const basePath =
                normalizedModelName === 'outsourced_orders.outsourcedordertemplate'
                    ? '/outsourced-orders/recurring/'
                    : '/collection-orders/recurring/';

            return `${basePath}?search=${encodeURIComponent(templateId)}`;
        },
        datatableRelatedModelName(element) {
            if (element.related_model_name) {
                if (!this.notEmptyObject(element.related_object)) {
                    return '-';
                }
                if (element.related_object.can_access === false) {
                    return element.related_object['name'];
                }
                let [module_name, model_name] = element.related_model_name.toString().split('.');
                let model = [module_name, model_name].join('-').toLowerCase();
                let url_module = module_name.split('_').join('-');
                let href = `#model=${model}&view=${element.related_object.id}`;
                let open_in_new_tab = false;
                if (
                    this.notEmptyObject(element.related_object) &&
                    element.related_model_name &&
                    element.related_model_name.includes('storage.StorageUnit') &&
                    !element.related_object.extra.establishment_id
                ) {
                    return `
                        <a data-location-id="${element.related_object.extra.disposal_location_id}" data-relation-id="${element.related_object.extra.relation_id}"
                            href="javascript:void(0)"
                           class="show-disposal-location"
                        >
                           ${element.related_object['name']}
                        </a>
                    `;
                } else {
                    if (this.isRecurringTemplateRelatedModel(element.related_model_name)) {
                        return `
                            <a
                                href="${this.getRecurringTemplateHref(
                                    element.related_model_name,
                                    element.related_object.id
                                )}"
                                target="_blank"
                                class="related-object-view"
                            >
                               ${element.related_object['name']}
                            </a>
                        `;
                    }
                    if (url_module.includes('orders')) {
                        if (model_name === 'Insight') {
                            href = '/collection-orders/insights/?filter_id=' + element.related_object.id;
                            open_in_new_tab = true;
                        } else {
                            href = `/${url_module}/dashboard/` + href;
                            open_in_new_tab = true;
                        }
                    }
                    if (url_module.includes('billing')) {
                        href = `/${url_module}/view/` + element.related_object.id + '/';
                        open_in_new_tab = true;
                    }
                    return `
                        <a data-id="${element.related_object.id}" data-model="${model}"
                           class="related-model-trigger related-object-view"
                           href=${href} target="${open_in_new_tab ? '_blank' : ''}"
                        >
                           ${element.related_object['name']}
                        </a>
                    `;
                }
            }
            return '-';
        },
        datatableTitle(element, data_model = 'task-task') {
            return `<a data-id="${element.id}"
                        data-model=${data_model}
                        class="related-model-trigger"
                        href="#model=${data_model}&view=${element.id}">
                        ${element.title}
                    </a>`;
        },
        datatableAssignees(element) {
            let self = this;
            if (element.assignees.length) {
                let assignees = element.assignees.map(function (a) {
                    return self.getAvatarOrInitials(a, 25);
                });
                if (assignees.length > 3) {
                    let assigneesLeft = assignees.length - 3;
                    return (
                        assignees.slice(0, 3).join(' ') +
                        `<div class="border border-dark rounded-circle d-inline-block"
                             style="width: 25px; height: 25px; text-align: center; margin-left: 3px;">
                             <span style="font-size: 12px;" data-bs-toggle="tooltip" title="${assigneesLeft} ${self.$__('more assignees')}">+${assigneesLeft}</span>
                        </div>`
                    );
                }
                return assignees.join(' ');
            }
            return '';
        },
        getFileExt(file) {
            if (file) {
                return '/static/images/file_icons/' + file.slice(((file.lastIndexOf('.') - 1) >>> 0) + 2) + '-icon.png';
            } else {
                return '';
            }
        },
        getAvatarOrInitials(person, size = 35) {
            function getInitials() {
                if (person.full_name) {
                    let splited = person.full_name.split(' ');
                    let result = '';
                    for (let s of splited) {
                        result += s[0] !== undefined ? s[0] : '';
                    }
                    return result;
                }
                if (person.email) {
                    return person.email[0];
                }
            }

            let html = `<div class="d-inline-block">`;
            if (person && person.avatar) {
                html += `<img class="round" src="${current_scheme}://${current_site}${person.avatar.file}"
                         alt="avatar" height="${size}" width="${size}" title="${person.full_name ? person.full_name : person.email ? person.email : '...'}">`;
            } else if (person && (person.full_name || person.email)) {
                html += `<div class="round d-flex m-auto justify-content-center"
                    style="width: ${size}px; height: ${size}px; background: rgb(230, 230, 226)"
                    title="${person.full_name ? person.full_name : person.email ? person.email : '...'}"
                    >
                        <div class="align-self-center" style="font-size: ${0.03 * size + 'rem'}; font-family: system-ui;cursor: pointer;">${getInitials()}</div>
                    </div>`;
            } else {
                html += `<img class="round" src='${current_scheme}://${current_site}/static/images/blank_user_avatar.png'
                         alt="avatar" height="${size}" width="${size}" title="${__('System')}">`;
            }
            html += '</div>';
            return html;
        },
        convertURIToObject() {
            let search = location.search.substring(1);
            if (!search) {
                return null;
            }
            return JSON.parse(
                '{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}'
            );
        },
        setFocusOnSearchField(className = null, ignoreVdt = false) {
            setTimeout(() => {
                if (className !== null && typeof className === 'string') {
                    $(`.${className}`).first().focus();
                    return;
                }
                if ($('.vdtnet-container input[type="search"]').length > 0 && !ignoreVdt) {
                    $('.vdtnet-container input[type="search"]').first().focus();
                } else {
                    $('input[type="search"]')
                        .filter(function () {
                            return $(this).closest('.mass-balance-establishment-select').length === 0;
                        })
                        .first()
                        .focus();
                }
            }, 500);
        },
        capitalize(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
        setQueryParams(key, val) {
            let hash = this.$route.hash;
            const query = Object.assign({}, this.$route.query);
            query[key] = val;
            this.$router.push({ query, ...{ hash: hash } }).catch(err => {});
        },
        unsetQueryParams(params = null) {
            let hash = this.$route.hash;
            const query = Object.assign({}, this.$route.query);
            if (params) {
                for (let key of Object.keys(query)) {
                    if (Array.isArray(params)) {
                        for (let param of params) {
                            if (param === key) {
                                delete query[key];
                            }
                        }
                    } else {
                        if (params == key) {
                            delete params[key];
                        }
                    }
                }
            } else {
                for (let key of Object.keys(query)) {
                    delete query[key];
                }
            }
            this.$router.push({ query, ...{ hash: hash } }).catch(err => {});
        },
        obj2base64(obj) {
            const jsonStr = JSON.stringify(obj);
            const utf8Bytes = new TextEncoder().encode(jsonStr); // UTF-8 encode the JSON string
            const binaryStr = String.fromCharCode.apply(null, utf8Bytes); // Convert Uint8Array to a binary string
            return btoa(binaryStr); // Base64 encode the binary string
        },
        orderNumberHtml(data = {}) {
            let self = this;
            let row = data.row;
            let orderType = data.orderType || 'collection';
            let uniqueClass = data.uniqueClass || '';
            let showRisk = data.showRisk || false;
            let canEditOrderNumber = data.canEditOrderNumber || false;
            let canViewOrder = data.canViewOrder !== false;
            let viewHref =
                data.viewHref ||
                `/${orderType}-orders/dashboard/#model=${orderType}_orders-${orderType}order&view=${row.id}`;

            let prepend = data.prependHtml || '';
            let appendHtml = data.appendHtml || '';
            let append = '';
            let warning = '';
            const isSensorCreated =
                orderType === 'collection' &&
                self.notEmptyObject(row.creator_json) &&
                row.creator_json.method === 'SENSOR';
            if (orderType == 'collection' && row.using_qr) {
                append += `<i class="fa fa-qrcode table-html-tooltips" title="${self.$__('Created using QR')}"></i> `;
            }
            if (row.is_portal === true) {
                if (self.notEmptyObject(row.creator_json)) {
                    append += `<i class="fa fa-user-circle table-html-tooltips" title="${row.creator_json.first_name} ${row.creator_json.last_name}"></i> `;
                } else {
                    append += `<i class="fa fa-user-circle table-html-tooltips" title="${row.creator.first_name} ${row.creator.last_name}"></i> `;
                }
            } else {
                if (isSensorCreated) {
                    append += `<i class="feather icon-database table-html-tooltips" title="${self.$__('Created using SENSOR')}"></i> `;
                }
            }

            let order_template = `${orderType}_order_template`;

            if (
                row[order_template] &&
                !isSensorCreated &&
                hasOwn(row[order_template], 'simplified_recurrence') &&
                row[order_template].simplified_recurrence &&
                row[order_template].simplified_recurrence.active === true
            ) {
                append += `<i class="fa fa-at table-html-tooltips cursor-pointer" title="${self.$__('Recurrent')}"></i> `;
            }
            if ((orderType === 'collection' || orderType === 'delivery') && showRisk) {
                if (hasOwn(row, 'has_risk') && row.has_risk) {
                    warning += `<i
                        class="feather icon-alert-triangle text-color-red"
                        title="${row.risk_description ? row.risk_description : ''}"
                        style="font-size: 1.35rem;margin-left: 2px">
                    </i>`;
                }
            }
            append = `${appendHtml}${append}${warning}`;
            let editOrderNumber = `
                <span style="cursor: pointer;" class="edit-order-number ${uniqueClass}" data-id="${row.id}">
                    <i class="fa fa-edit"></i>
                </span>
            `;
            if (orderType === 'outsourced') {
                if (row.is_accepted === true) {
                    append += `<i class="feather icon-check-circle table-html-tooltips cursor-pointer" style="color: green" title="${self.$__('Accepted')}"></i> `;
                } else if (row.is_accepted === false) {
                    append += `<i class="feather icon-x table-html-tooltips cursor-pointer text-color-red" title="${self.$__('Declined')}"></i> `;
                } else {
                    append += `<i class="feather icon-alert-circle table-html-tooltips cursor-pointer" title="${self.$__('Unknown')}"></i> `;
                }
            }
            let html = `
                <div style="white-space: nowrap">
                    ${prepend}
                    ${
                        canViewOrder
                            ? `<a target="_blank" data-${orderType}-order-id="${row.id}" class="${orderType}-order-view" href="${viewHref}">${row.order_number}</a>`
                            : `<span>${row.order_number}</span>`
                    }
                    ${append}
                </div>`;
            if (orderType === 'outbound' && row.evoa_transport) {
                html += '';
            } else {
                html += `
                    ${row.status < 4 && canEditOrderNumber && (orderType === 'delivery' || orderType === 'outbound') ? editOrderNumber : ''}
                `;
            }
            return html;
        },
        base642obj(base) {
            return JSON.parse(atob(base));
        },
        previousYear() {
            return this.currentYear() - 1;
        },
        currentYear() {
            return new Date().getFullYear();
        },
        cloneObj(obj) {
            return JSON.parse(JSON.stringify(obj));
        },
        hexToRGB(hex, alpha) {
            let r = parseInt(hex.slice(1, 3), 16),
                g = parseInt(hex.slice(3, 5), 16),
                b = parseInt(hex.slice(5, 7), 16);

            if (alpha) {
                return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
            } else {
                return 'rgb(' + r + ', ' + g + ', ' + b + ')';
            }
        },
        isOverdueDate(date) {
            return moment(date).isBefore(moment(), 'day');
        },
        isOverdueDateTime(dateTime) {
            return moment(dateTime).isBefore(moment());
        },
        calcOccurrencesInString(str, search = ',') {
            let count, index;
            for (count = -1, index = -2; index != -1; count++, index = str.indexOf(search, index + 1));
            return count;
        },
        convertToNumber(str, otherSymbols = '.,', onlyOnFirstPosition = '+-') {
            /*
             * Configurable method that converts string of any letters to string of numbers
             * send otherSymbols to allow on any position
             * send onlyOnFirstPosition to allow only on index === 0
             * */
            let truncated = '';
            let index = 0;
            for (let letter of str) {
                index++;
                if (
                    (letter >= '0' && letter <= '9') ||
                    otherSymbols.includes(letter) ||
                    onlyOnFirstPosition.includes(letter)
                ) {
                    if (index > 1 && onlyOnFirstPosition.includes(letter)) {
                        continue;
                    }
                    truncated += letter;
                }
            }
            return truncated;
        },
        isValNumeric(val) {
            let parsedVal = parseFloat(val);
            return !isNaN(parsedVal) && isFinite(parsedVal);
        },
        areValidNumbers(...vals) {
            for (let val of vals) {
                if (typeof val !== 'number' || isNaN(val)) {
                    return false;
                }
            }
            return true;
        },
        withPopper(dropdownList, component, { width }) {
            dropdownList.style.width = width;
            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'top',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -1],
                        },
                    },
                    {
                        name: 'toggleClass',
                        enabled: true,
                        phase: 'write',
                        fn({ state }) {
                            component.$el.classList.toggle('drop-up', state.placement === 'top');
                        },
                    },
                ],
            });
            return () => popper.destroy();
        },
        preventNegativeNumberInput(propertyPath) {
            let propArray = propertyPath.split('.');
            let obj = this;
            for (let i = 0; i < propArray.length - 1; i++) {
                obj = obj[propArray[i]];
            }

            let value = obj[propArray[propArray.length - 1]];
            // If the field is empty, set the value to an empty string
            if (value === '') {
                obj[propArray[propArray.length - 1]] = '';
                return;
            }

            // If the value is still negative after removing extra '-' signs, set it to 0
            if (Number(value) < 0) {
                obj[propArray[propArray.length - 1]] = 0;
            } else {
                obj[propArray[propArray.length - 1]] = Number(value);
            }
        },
        preventNegativeTextInput(propertyPath) {
            let propArray = propertyPath.split('.');
            let obj = this;
            for (let i = 0; i < propArray.length - 1; i++) {
                obj = obj[propArray[i]];
            }
            let value = obj[propArray[propArray.length - 1]];
            let newValue = value.replace(/\D/g, '');
            if (value !== newValue) {
                obj[propArray[propArray.length - 1]] = newValue;
            }
        },
        joinErrorMessages(errorData) {
            const messages = [];

            const addMessage = value => {
                if (value === null || typeof value === 'undefined') {
                    return;
                }
                const text = String(value).trim();
                if (text) {
                    messages.push(text);
                }
            };

            const walk = (value, keyPath) => {
                if (value === null || typeof value === 'undefined') {
                    return;
                }
                if (Array.isArray(value)) {
                    if (value.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
                        addMessage(`${keyPath}: ${value.join(' ')}`);
                        return;
                    }
                    value.forEach(item => walk(item, keyPath));
                    return;
                }
                if (typeof value === 'object') {
                    Object.keys(value).forEach(childKey => {
                        walk(value[childKey], keyPath ? `${keyPath}.${childKey}` : childKey);
                    });
                    return;
                }
                addMessage(`${keyPath}: ${value}`);
            };

            if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
                Object.keys(errorData).forEach(key => {
                    walk(errorData[key], key);
                });
            } else {
                walk(errorData, 'error');
            }

            return messages.join('\n');
        },
    },
    mounted() {
        this.filterKeypressHandler = event => {
            if (
                typeof this.triggerFiltrate !== 'undefined' &&
                this.show_filters === true &&
                event.key === 'Enter' &&
                event.code === 'Enter'
            ) {
                this.triggerFiltrate();
            }
        };
        window.addEventListener('keypress', this.filterKeypressHandler, false);
    },
    beforeUnmount() {
        if (this.filterKeypressHandler) {
            window.removeEventListener('keypress', this.filterKeypressHandler, false);
            this.filterKeypressHandler = null;
        }
    },
    filters: {
        capitalize: function (value) {
            if (!value) return '';
            value = value.toString();
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
        uppercase: function (value) {
            if (!value) return '';
            value = value.toString();
            return value.toUpperCase();
        },
    },
    directives: {
        case: {
            update: function (el, key) {
                if (key.value.toLowerCase() === 'upper') {
                    let old_value = el.value;
                    el.value = old_value.toUpperCase();
                } else if (key.value.toLowerCase() === 'lower') {
                    let old_value = el.value;
                    el.value = old_value.toLowerCase();
                }
            },
        },
    },
};

const order_utils = {
    mixins: [currency_mixin],
    methods: {
        setUniqueClass() {
            this.uniqueClass = this.generateUniqueIdent();
        },
        async emitWasteStreamRowUpdate(node, data) {
            try {
                await this.waste_streams_datatable.row(node).data(data).draw();
            } catch (e) {
                await this.datatable.api.row(node).data(data).draw();
            }
        },
        calcFromDensity: function (base, unit, density) {
            if (unit.toLowerCase() === 'kg') {
                return (base / density).toFixed(0);
            }
            if (unit.toLowerCase() === 'l') {
                return (base * density).toFixed(0);
            }
        },
        deleteExtraItem: function (row) {
            if (hasOwn(this, 'unsetWatchersForTotalField')) {
                this.unsetWatchersForTotalField();
            }
            this.order.extra_items = this.order.extra_items.filter(extra_item => {
                return extra_item !== row;
            });
        },
        extraItemTotalPrice: function (row) {
            if (!row.item || !this.notEmptyObject(row.item)) {
                return '';
            }
            let total = row.quantity * (row.price ? row.price : row.item.price);
            if (isNaN(total)) {
                return '';
            }
            return this.currencyToString(total);
        },
        fetchNoEuralCodeItemsOptionsBilling: function (main_item_price_configuration_as_of = null) {
            let self = this;
            let planned_date = this.order.planned_date
                ? this.order.planned_date
                : this.order.planned_date
                  ? this.order.planned_date
                  : null;
            let created_at = this.order.created_at ? this.order.created_at.slice(0, 10) : null;
            if (main_item_price_configuration_as_of) {
                if (main_item_price_configuration_as_of == 3) {
                    // toISOString() converts to UTC first, so a naive
                    // new Date().toISOString().slice(0, 10) rolls over to
                    // the wrong calendar day for evening local times (see
                    // issue GH-2008 Phase 3) - offset-compensate first, like
                    // DaysCalendar.vue:toDatetimeLocal already does.
                    const now = new Date();
                    const today = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
                    created_at = today;
                }
                if (main_item_price_configuration_as_of === 2) {
                    created_at = this.order.created_at.split('T')[0];
                }
            }
            let fields = null;
            if (this.itemPriceConfigurationFields) {
                fields = `fields=${this.obj2base64(this.itemPriceConfigurationFields)}`;
            }

            agent.Catalog.getNoEuralCodeItemsConfigurations(
                this.order.debtor.id,
                3, // item type
                created_at ? created_at : planned_date,
                fields
            ).then(function (result) {
                self.no_eural_code_items_options = result.data;
            });
        },
        fetchNoEuralCodeItemsOptions: function () {
            let self = this;
            let planned_date = this.order.planned_date
                ? this.order.planned_date
                : this.order.planned_date
                  ? this.order.planned_date
                  : null;
            let created_at = this.order.created_at ? this.order.created_at.slice(0, 10) : null;
            let fields = null;
            if (this.itemPriceConfigurationFields) {
                fields = `fields=${this.obj2base64(this.itemPriceConfigurationFields)}`;
            }

            agent.Catalog.getNoEuralCodeItemsConfigurations(
                this.order.debtor.id,
                3, // item type
                created_at ? created_at : planned_date,
                fields
            ).then(function (result) {
                self.no_eural_code_items_options = result.data;
            });
        },
        checkExceededInvoices: function (order_type, debtor_id) {
            let self = this;
            agent.Orders.checkExceededInvoices(order_type, debtor_id).then(function (result) {
                self.exceeded_invoices_exists = result.data.exceeded_invoices_exists;
            });
        },
        addExtraItem: function () {
            this.fetchNoEuralCodeItemsOptions();
            this.order.extra_items.push({
                item: null,
                price: null,
                ledger: null,
                is_charged: false,
                is_default: false,
                vat_code: null,
                has_error: false,
                quantity: null,
            });
        },
        validateExtraItems() {
            let has_error = false;
            for (let item of this.order.extra_items) {
                if (!item.quantity || item.quantity === '' || !item.item.price || item.item.price === '') {
                    has_error = true;
                    item.has_error = true;
                } else {
                    item.has_error = false;
                }
            }
            return !has_error;
        },
        presetVatCode: function (row) {
            if (row.ledger) {
                let vat_code = this.vat_types.find(x => x.code == row.ledger.vat_code);
                if (vat_code) {
                    row.vat_code = vat_code;
                }
            }
        },
        forceUpdate: function () {
            let self = this;
            setTimeout(function () {
                self.$forceUpdate();
            });
        },
        formattedRecurrence: function (recurrence) {
            let html = recurrence;
            return html;
        },
        calcMainItemTotal(event) {
            if (
                this.order.main_item_fixed_price_per_unit_override &&
                this.order.main_item_fixed_price_per_unit_override.length
            ) {
                this.order.main_item_fixed_total_override = null;
                this.order.main_item_fixed_price_per_unit_override = this.simpleSanitizer(
                    this.order.main_item_fixed_price_per_unit_override
                );
            } else {
                this.order.main_item_fixed_price_per_unit_override = null;
            }
            const newVal = parseFloat(this.order.main_item_fixed_price_per_unit_override);
            let weight = this.order.actual_weight ? this.order.actual_weight : this.order.planned_weight;
            this.order.main_item_total = isNaN(newVal)
                ? null
                : parseFloat((parseFloat(this.order.main_item_fixed_price_per_unit_override) * weight).toFixed(2));
        },
        calcPricePerUnit(evt) {
            this.order.main_item_fixed_price_per_unit_override = null;

            if (evt.target.value) {
                this.order.main_item_total = evt.target.value.replace(/,/g, '.');
            }
        },
        handleSelectWasteStream(that, checkRegion = false) {
            let self = this;
            let data = null;
            this.order.main_item = null;
            $(that)
                .parents()
                .each(function () {
                    if (this.tagName === 'TR') {
                        if (self.waste_streams_datatable && self.waste_streams_datatable.row(this).node()) {
                            data = self.waste_streams_datatable.row(this).data();
                        }
                    }
                });
            if (data) {
                if (checkRegion) {
                    self.checkIsOutsourcedOrder(data, () => {
                        if (data.has_risk && (!data.risk_description || data.risk_description === '')) {
                            Swal.fire({
                                title: self.$__('Impossible to select waste stream with risk'),
                                html: `<span>${self.$__('Fill risk description and try again')}</span>`,
                            });
                        } else {
                            this.waste_stream_changed = true;
                            agent.WasteStream.get($(that).attr('data-waste-stream-id')).then(function (result) {
                                self.order.waste_stream = result.data;
                            });
                        }
                    });
                } else {
                    if (data.has_risk && (!data.risk_description || data.risk_description === '')) {
                        Swal.fire({
                            title: self.$__('Impossible to select waste stream with risk'),
                            html: `<span>${self.$__('Fill risk description and try again')}</span>`,
                        });
                    } else {
                        this.waste_stream_changed = true;
                        agent.WasteStream.get($(that).attr('data-waste-stream-id')).then(function (result) {
                            self.order.waste_stream = result.data;
                        });
                    }
                }
            }
        },
        handleWasteStreamRiskChange(that, posX = null, posY = null) {
            function simulateClick(element, x, y) {
                // Create a new MouseEvent
                let event = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    clientX: x,
                    clientY: y,
                });

                // Dispatch the event on the element
                element.dispatchEvent(event);
            }

            let self = this;
            let id = $(that).data('waste-stream-id');
            let node = null,
                data = null;
            $(that)
                .parents()
                .each(function () {
                    if (this.tagName === 'TR') {
                        try {
                            if (self.waste_streams_datatable.row(this).node()) {
                                node = self.waste_streams_datatable.row(this).node();
                                data = self.waste_streams_datatable.row(this).data();
                            }
                        } catch (error) {
                            if (self.datatable.api.row(this).node()) {
                                node = self.datatable.api.row(this).node();
                                data = self.datatable.api.row(this).data();
                            }
                        }
                    }
                });
            let upd_data = null;
            if (!data.has_risk && hasOwn(self, 'openRiskDescription') && self.openRiskDescription) {
                const element = node.querySelector('.edit-risk-description');
                if (element) {
                    simulateClick(element, posX, posY);
                }
            } else {
                if (data && node) {
                    upd_data = { ...data, ...{} };
                }
                agent.WasteStream.changeRiskState(id).then(function (result) {
                    if (upd_data) {
                        upd_data.has_risk = result.data;
                        self.emitWasteStreamRowUpdate(node, upd_data);
                    }
                });
            }
        },
        handleInvalidateWasteStream(that) {
            let self = this;
            let id = $(that).data('waste-stream-id');
            let [node, data] = [null, null];
            $(that)
                .parents()
                .each(function () {
                    if (this.tagName === 'TR') {
                        if (self.waste_streams_datatable.row(this).node()) {
                            node = self.waste_streams_datatable.row(this).node();
                            data = self.waste_streams_datatable.row(this).data();
                        }
                    }
                });
            Swal.fire({
                title: self.$__('Deactivating a waste stream'),
                html: `
                    <div class="row">
                        <div class="col-12">
                            ${self.$__('You are about to deactivate waste stream number')} ${data.waste_stream_number} \
                            ${self.$__('with eural code')} ${data.eural_code.full_code} ${self.$__('for relation')} ${data.disposer.name}. \
                            ${self.$__("This process can't be reverted. Are you sure?")}
                        </div>
                    </div>
                `,
                customClass: 'swal-wide',
                showCancelButton: true,
                showConfirmButton: true,
                cancelButtonText: self.$__('Cancel'),
                confirmButtonText: self.$__("I'm sure"),
            }).then(function (result) {
                if (result.isConfirmed) {
                    let upd_data = null;
                    if (data && node) {
                        upd_data = { ...data, ...{} };
                    }
                    agent.WasteStream.invalidateWasteStream(id)
                        .then(function (result) {
                            if (upd_data) {
                                upd_data.is_valid = result;
                                self.emitWasteStreamRowUpdate(node, upd_data);
                            }
                        })
                        .catch(function (error) {
                            // check if error is 409
                            if (error.response?.status === 409) {
                                Swal.fire({
                                    title: self.$__("Can't invalidate waste stream. It is has open orders."),
                                    icon: 'info',
                                });
                            }
                        });
                }
            });
        },
        simpleSanitizer(value, decLimit = 5) {
            value += '';
            value = this.convertToNumber(value);
            if (value.length) {
                value = value.split(',').join('.');
                let split = value.split('.');
                if (split.length > 1) {
                    let secPart = split[1];
                    if (secPart.length > decLimit) {
                        secPart = secPart.slice(0, decLimit);
                    }
                    value = `${split[0]}.${secPart}`;
                } else {
                    value = split[0];
                }
            }
            return value;
        },
        calculatePricePerUnit() {
            let price_per_unit = 0;
            let actual_amount = null;
            if (this.order.actual_weight || this.order.actual_weight === 0) {
                actual_amount = this.order.actual_weight;
            }
            let estimated_amount = 0;
            if (this.order.planned_weight || this.order.planned_weight === 0) {
                estimated_amount = this.order.planned_weight;
            }
            if (actual_amount || actual_amount === 0) {
                estimated_amount = actual_amount;
            }
            if (this.order.main_item) {
                if (this.order.main_item.is_price_tier) {
                    if (
                        this.order.main_item.minimum_total_quantity &&
                        this.order.main_item.initial_invoice_price &&
                        estimated_amount <= this.order.main_item.minimum_total_quantity
                    ) {
                        price_per_unit = parseFloat(this.order.main_item.initial_invoice_price) / estimated_amount;
                    } else if (this.order.main_item.tier.find(x => x.is_fixed) === undefined) {
                        let total_price = 0;

                        // VOLUME PRICING
                        this.order.main_item.tier
                            .filter(x => !x.is_fixed)
                            .forEach(function (x) {
                                if (estimated_amount >= x.quantity_from && estimated_amount <= x.quantity_to) {
                                    total_price += estimated_amount * parseFloat(x.price);
                                }
                            });

                        // CORRECT PRICE TIER CALCULATION
                        // this.order.main_item.tier.forEach(function (x) {
                        //     if (estimated_amount >= x.quantity_from && estimated_amount > x.quantity_to) {
                        //         total_price += ((x.quantity_to - x.quantity_from) + 1) * parseFloat(x.price)
                        //     } else if (estimated_amount >= x.quantity_from && estimated_amount <= x.quantity_to) {
                        //         total_price += ((estimated_amount - x.quantity_from) + 1) * parseFloat(x.price)
                        //     }
                        // })
                        price_per_unit = total_price / estimated_amount;
                    } else {
                        let total_price = 0;
                        let fixed_price_found = false;
                        this.order.main_item.tier
                            .filter(x => x.is_fixed)
                            .forEach(function (x) {
                                if (estimated_amount >= x.quantity_from && estimated_amount <= x.quantity_to) {
                                    total_price += parseFloat(x.price);
                                    fixed_price_found = true;
                                }
                            });
                        if (!fixed_price_found) {
                            // VOLUME PRICING
                            this.order.main_item.tier
                                .filter(x => !x.is_fixed)
                                .forEach(function (x) {
                                    if (estimated_amount >= x.quantity_from && estimated_amount <= x.quantity_to) {
                                        total_price += estimated_amount * parseFloat(x.price);
                                    }
                                });
                        }
                        price_per_unit = total_price / estimated_amount;
                    }
                } else {
                    price_per_unit = parseFloat(this.order.main_item.price);
                    if (
                        this.order.main_item.initial_invoice_price &&
                        estimated_amount * price_per_unit < parseFloat(this.order.main_item.initial_invoice_price)
                    ) {
                        price_per_unit = parseFloat(this.order.main_item.initial_invoice_price) / estimated_amount;
                    }
                }
            }
            if (price_per_unit === Infinity) {
                return 0;
            }
            if (isNaN(price_per_unit)) {
                return 0;
            }
            return price_per_unit;
        },
        checkAnalysisBeforeProcess(analysis) {
            if (
                !analysis.analysis_results ||
                (Array.isArray(analysis.analysis_results) && analysis.analysis_results.length === 0)
            ) {
                return true;
            }
            let notConfirmed = analysis.analysis_results.findIndex(a => a.confirmed === false);
            return notConfirmed === -1;
        },
        checkFailedAnalysisExists(analysis) {
            if (
                !analysis.analysis_results ||
                (Array.isArray(analysis.analysis_results) && analysis.analysis_results.length === 0)
            ) {
                return true;
            }
            let notConfirmed = analysis.analysis_results.findIndex(a => a.failed === true && a.confirmed === true);
            let notFailed = notConfirmed === -1;
            if (notFailed) {
                analysis.analysis_results.forEach(function (row) {
                    if (row.confirmed) {
                        if (row.analysis_item.benchmark && row.analysis_item.operator && row.result) {
                            let color = '';
                            if (row.analysis_item.operator.name === '<') {
                                if (parseFloat(row.result) >= parseFloat(row.analysis_item.benchmark)) {
                                    notFailed = false;
                                }
                            } else if (row.analysis_item.operator.name === '>') {
                                if (parseFloat(row.result) <= parseFloat(row.analysis_item.benchmark)) {
                                    notFailed = false;
                                }
                            } else {
                                if (parseFloat(row.result) !== parseFloat(row.analysis_item.benchmark)) {
                                    notFailed = false;
                                }
                            }
                        }
                    }
                });
            }

            return notFailed;
        },
    },
    computed: {
        selectedWasteStreamProcessingMethod: function () {
            let selectedWasteStreamProcessingMethod = {};
            if (this.notEmptyObject(this.order.processing_method_json)) {
                selectedWasteStreamProcessingMethod = this.order.processing_method_json;
            } else if (
                this.order.waste_stream &&
                this.order.waste_stream.destination_establishment &&
                this.order.waste_stream.eural_code.processing_methods.length > 0
            ) {
                selectedWasteStreamProcessingMethod = this.order.waste_stream.eural_code.processing_methods.find(
                    processing_method => {
                        return processing_method.establishment === this.order.waste_stream.destination_establishment.id;
                    }
                );
            } else if (
                this.order.waste_stream &&
                this.order.waste_stream.destination &&
                this.order.waste_stream.eural_code.outbound_processing_methods.length > 0
            ) {
                selectedWasteStreamProcessingMethod =
                    this.order.waste_stream.eural_code.outbound_processing_methods.find(processing_method => {
                        return processing_method.destination === this.order.waste_stream.destination.id;
                    });
            }
            return selectedWasteStreamProcessingMethod == null ? {} : selectedWasteStreamProcessingMethod;
        },
        plannedSpeedPerHour: function () {
            if (this.order.planned_weight && this.order.planned_loading_time_minutes) {
                this.order.planned_speed_kg_hour = (
                    (this.order.planned_weight / this.order.planned_loading_time_minutes) *
                    60
                ).toFixed(0);
                return this.order.planned_speed_kg_hour;
            }
            return '';
        },
        mainItemEstimatedPricePerUnit: function () {
            if (
                this.order.main_item_fixed_price_per_unit_override !== null &&
                this.order.main_item_fixed_price_per_unit_override !== undefined &&
                this.order.main_item_fixed_price_per_unit_override !== ''
            ) {
                return this.order.main_item_fixed_price_per_unit_override;
            }
            if (
                this.order.main_item_fixed_total_override !== null &&
                this.order.main_item_fixed_total_override !== undefined &&
                this.order.main_item_fixed_total_override !== ''
            ) {
                let actual_amount = null;
                if (this.order.actual_weight || this.order.actual_weight === 0) {
                    actual_amount = this.order.actual_weight;
                }
                let estimated_amount = 0;
                if (this.order.planned_weight || this.order.planned_weight === 0) {
                    estimated_amount = this.order.planned_weight;
                }
                if (actual_amount || actual_amount === 0) {
                    estimated_amount = actual_amount;
                }
                return this.order.main_item_fixed_total_override / estimated_amount;
            }
            return this.calculatePricePerUnit();
        },
        debtorMustBeChanged: function () {
            let self = this;
            let res = false;
            self.alternative_debtor = null;
            let fields = '';
            if (this.order.id) {
                return false;
            }
            if (hasOwn(self, 'defaultRelationFields')) {
                fields = `fields=${self.defaultRelationFields}`;
            }
            if (this.order.main_item && this.order.alternative_debtors) {
                this.order.alternative_debtors.some(function (debtor) {
                    debtor.waste_groups.some(function (group) {
                        if (self.order.main_item.item.collection_waste_group) {
                            if (group.id === self.order.main_item.item.collection_waste_group.id) {
                                self.isWLoading = true;
                                agent.Relation.getRelation(debtor.debtor.id, fields, {
                                    permission_scope: self.orderType || 'collection',
                                }).then(function (result) {
                                    if (!self.is_debtor_manual_checked || self.order.debtor.id === result.data.id) {
                                        self.order.debtor = result.data;
                                        self.is_debtor_manual_checked = false;
                                    } else {
                                        self.alternative_debtor = result.data;
                                    }
                                    self.isWLoading = false;
                                });
                                res = true;
                            }
                        }
                    });
                });
            }
            if (
                !this.order.main_item &&
                this.notEmptyObject(this.order.disposer) &&
                self.scope === 'main_item' &&
                !this.collection_order_template_unset &&
                !this.waste_stream_changed &&
                !this.is_debtor_manual_checked
            ) {
                agent.Relation.getRelation(this.order.disposer.id, fields, {
                    permission_scope: self.orderType || 'collection',
                }).then(function (result) {
                    if (result.data.invoice_debtor && self.notEmptyObject(result.data.invoice_debtor)) {
                        self.order.debtor = result.data.invoice_debtor;
                    } else if (
                        result.data.billing_address &&
                        (result.data.billing_address.street_name ||
                            result.data.billing_address.postal_code ||
                            result.data.billing_address.place)
                    ) {
                        self.order.debtor = result.data;
                    }
                });
            }
            this.collection_order_template_unset = false;
            this.waste_stream_changed = false;
            this.debtorWasChanged = false;
            return res;
        },
        getPriceConfigurationPlace() {
            if (!this.notEmptyObject(this.order.main_item)) {
                return null;
            }
            let configuration = this.order.main_item;
            if (!configuration.price_list && !configuration.price_list_revision && !configuration.relation) {
                return this.$__('General');
            }
            if (configuration.price_list || configuration.price_list_revision) {
                let option = this.$__('Price list');
                if (configuration.price_list && configuration.price_list.name) {
                    return `${option} – ${configuration.price_list.name}`;
                }
                return option;
            }
            if (configuration.relation) {
                return this.$__('Price agreement');
            }
        },
        priceConfigurationType() {
            if (!this.notEmptyObject(this.order.main_item)) {
                return null;
            }
            let configuration = this.order.main_item;
            if (!configuration.price_list && !configuration.price_list_revision && !configuration.relation) {
                return 'general';
            }
            if (configuration.price_list || configuration.price_list_revision) {
                return 'price_list';
            }
            if (configuration.relation) {
                return 'price_agreement';
            }
        },
    },
    beforeUnmount() {
        $(document).off('change', '.global-change-waste-stream-risk');
    },
};

const messages = {
    methods: {
        showToast(args) {
            const instance = getCurrentInstance();
            let title = 'title' in args ? args['title'] : 'Notification',
                variant = 'variant' in args ? args['variant'] : 'success',
                position = 'position' in args ? args['position'] : POSITION.TOP_RIGHT,
                icon = 'icon' in args ? args['icon'] : 'icon-briefcase',
                text = 'text' in args ? args['text'] : '',
                html = 'html' in args ? args['html'] : false,
                timeout = 'timeout' in args ? args['timeout'] : 2000;
            const regex = /(<([^>]+)>)/gi;
            text = text ? (html ? text : text.replace(regex, '')) : title;
            const toast = this.$toast || this.$root?.$toast || useToast();
            if (!toast) {
                console.warn('[toast unavailable]', { title, text, variant, icon });
                return;
            }
            toast(
                {
                    component: ToastificationContent,
                    props: {
                        title: title ? title : 'Notification',
                        icon: icon ? icon : 'icon-briefcase',
                        text: text,
                        html,
                        variant,
                    },
                },
                {
                    position: position,
                    icon: false,
                    timeout: timeout,
                }
            );
        },
    },
};

const event_parser = {
    data() {
        return {
            available_linking_objects: [
                'notification',
                'employee',
                'task',
                'sale',
                'contact',
                'relation',
                'user',
                'transportunit',
                'storageunit',
                'invoice',
                'collectionorder',
                'deliveryorder',
                'outboundorder',
                'outsourcedorder',
                'bulk',
            ],
        };
    },
    mounted() {
        $(document).off('click', '.toggle-whole-event-code');
        $(document).on('click', '.toggle-whole-event-code', function () {
            $(this).parent().find('.event-code-container').toggleClass('d-none');
            $(this).parent().find('.label-toggler').toggleClass('d-none');
            $(this).find('h5').toggleClass('text-sm');
        });
        $(document).off('click', '.toggle-whole-event-code-timeline');
        $(document).on('click', '.toggle-whole-event-code-timeline', function (evt) {
            evt.stopImmediatePropagation();
            $(this)
                .parent()
                .parent()
                .parent()
                .parent()
                .parent()
                .parent()
                .find('.event-code-container')
                .toggleClass('d-none');
            $(this).parent().find('.toggle-whole-event-code-timeline').toggleClass('d-none');
        });
    },
    methods: {
        parseObject(obj, inline = false) {
            let classInline = inline ? 'd-inline' : '';
            let self = this;
            let html = '';
            for (let [k, v] of Object.entries(obj)) {
                if (Array.isArray(v)) {
                    try {
                        html += `${self.$__(k)}: ${self.parseArray(v)}`;
                    } catch (TypeError) {
                        html += `${k}: ${self.parseArray(v)}`;
                    }
                } else if (typeof v === 'object' && v !== null) {
                    try {
                        html += `<p class="m-0 ${classInline}">${self.$__(k)}: <div class="ms-1">${self.parseObject(v, true)}</div></p>`;
                    } catch (TypeError) {
                        html += `<p class="m-0 ${classInline}">${k}: <div class="ms-1">${self.parseObject(v, true)}</div></p>`;
                    }
                } else if (v !== null) {
                    try {
                        html += `<p class="m-0 ${classInline}">${self.$__(k)}: ${v}${inline ? '; ' : ''}</p>`;
                    } catch (TypeError) {
                        html += `<p class="m-0 ${classInline}">${k}: ${v}${inline ? '; ' : ''}</p>`;
                    }
                }
            }
            return html;
        },
        parseArray(arr) {
            let self = this;
            let html = '';
            for (let obj of arr) {
                html += `<div class="small event-arr-obj">`;
                if (typeof obj === 'string') {
                    try {
                        html += ` ${self.$__(obj)} `;
                    } catch (TypeError) {
                        html += ` ${obj} `;
                    }
                } else if (Array.isArray(obj)) {
                    html += self.parseArray(obj);
                } else if (typeof obj === 'object' && obj !== null) {
                    html += `<div class="m-0">${self.parseObject(obj)}</div>`;
                }
                html += `</div>`;
            }
            return html;
        },
        parseEventCode(eventCode) {
            let self = this;
            let html = "<div class='event-code-container row ms-0'>";

            function recurse(log = {}) {
                html += '<div class="col-lg-12"><div class="row d-flex justify-content-center">';
                // Don't add expanded section if there's nothing to show; might be a hack
                if (Object.entries(eventCode).some(e => typeof e[1] === 'object')) {
                    for (const [key, value] of Object.entries(log)) {
                        if (Array.isArray(value)) {
                            if (key.trim()[0] === '-' || key.trim()[0] === '+') {
                                let color = key.trim()[0] === '+' ? 'green' : 'red';
                                try {
                                    html += `<div class='${color} col-lg-6 cut-text'>${self.$__(key)}: ${self.parseArray(value)}</div>`;
                                } catch (TypeError) {
                                    html += `<div class='${color} col-lg-6 cut-text'>${key}: ${self.parseArray(value)}</div>`;
                                }
                            } else {
                                try {
                                    html += `<div class='yellow col-lg-6'>${self.$__(key)}: ${self.parseArray(value)}</div>`;
                                } catch (TypeError) {
                                    html += `<div class='yellow col-lg-6'>${key}: ${self.parseArray(value)}</div>`;
                                }
                            }
                        } else if (typeof value === 'object' && value !== null) {
                            if (key.trim()[0] === '-' || key.trim()[0] === '+') {
                                let color = key.trim()[0] === '+' ? 'green' : 'red';
                                try {
                                    html += `<div class='${color} col-lg-6 cut-text'>${self.$__(key)}: ${self.parseObject(value, true)}</div>`;
                                } catch (TypeError) {
                                    html += `<div class='${color} col-lg-6 cut-text'>${key}: ${self.parseObject(value, true)}</div>`;
                                }
                            } else {
                                try {
                                    html += `<h5 class="w-100 text-center">${self.$__(key)} - ${self.$__('updated')}</h5>`;
                                } catch (TypeError) {
                                    html += `<h5 class="w-100 text-center">${key} - ${'updated'}</h5>`;
                                }
                                recurse(value);
                            }
                        } else {
                            if (value !== null) {
                                if (key.trim()[0] === '-' || key.trim()[0] === '+') {
                                    let color = key.trim()[0] === '+' ? 'green' : 'red';
                                    try {
                                        html += `<div class='${color} col-lg-6 cut-text'>${self.$__(key)}: ${self.$__(value)}</div>`;
                                    } catch (TypeError) {
                                        html += `<div class='${color} col-lg-6 cut-text'>${key}: ${value}</div>`;
                                    }
                                } else {
                                    try {
                                        html += `<h3 class="w-100 text-center">${self.$__(key)} - ${self.$__(value)}</h3>`;
                                    } catch (TypeError) {
                                        html += `<h3 class="w-100 text-center">${key} - ${self.$__(value)}</h3>`;
                                    }
                                }
                            }
                        }
                    }
                }
                html += '</div></div>';
            }

            if (!(typeof eventCode === 'string')) recurse(eventCode);
            html += '</div>';
            return `${html}`;
        },
        getEventTitle(eventCode) {
            let self = this;
            // No expand if there's nothing to show; might be a hack;
            let html = Object.entries(eventCode).some(e => typeof e[1] === 'object')
                ? "<div class='mt-1 toggle-whole-event-code'>"
                : "<div class='mt-1'>";
            // HACK for wrong error messages in database
            if (typeof eventCode == 'string') html += `<h5>${self.$__(eventCode)}</h5>`;
            else {
                for (let [key, value] of Object.entries(eventCode)) {
                    if (typeof value === 'object' && value !== null) {
                        html += `<h5>${self.$__(key)} - ${self.$__('updated')}</h5>`;
                    } else {
                        html += `<h5>${self.$__(key)} - ${self.$__(value)}</h5>`;
                    }
                }
            }
            // No expand if there's nothing to show; might be a hack;
            if (Object.entries(eventCode).some(e => typeof e[1] === 'object')) {
                html += `<div class='small label-toggler'>(${self.$__('Click to expand')})</div>`;
                html += '</div>';
            }
            return `${html}`;
        },
        getTitleForInsightEvents(extended_item) {
            let result = this.$__('Insight for <some_eural_code> at <some_address>');
            let eural_code = extended_item.customer.eural_code.full_code;
            let address = extended_item.customer.origin.single_line;
            result = result.replace('<some_eural_code>', eural_code);
            result = result.replace('<some_address>', address);
            return result;
        },
        getTitleForTimeline(eventCode) {
            let self = this;
            let somethingToExpand = false;
            let html = `<div>`;
            for (let [key, value] of Object.entries(eventCode)) {
                if (typeof value === 'object' && value !== null) {
                    somethingToExpand = true;
                    html += `<b>${self.$__(key + "'s")}: </b>`;
                    for (let k of Object.keys(value)) {
                        let color =
                            k.trim()[0] === '+' ? 'bg-success' : k.trim()[0] === '-' ? 'bg-danger' : 'bg-primary';
                        if (k.trim() === 'errors') color = 'bg-warning';
                        html += `<span class="badge rounded-pill ${color}">${self.$__(k)}</span>`;
                    }
                    html += `<span> ${self.$__('were affected. ')}</span>`;
                } else {
                    html += `<span>${self.$__(key)} ${self.$__(value)}</span>`;
                }
            }
            if (somethingToExpand) {
                html += `<span class="toggle-whole-event-code-timeline"><i class="fa fa-angle-down"></i> ${self.$__('Expand')}</span>
                     <span class="toggle-whole-event-code-timeline d-none"><i class="fa fa-angle-up"></i> ${self.$__('Collapse')}</span>`;
            }
            html += `</div>`;
            return html;
        },
        getLinkToObject(item, innerText = 'show') {
            if (!item.preview_link) {
                return '';
            }
            if (!this.available_linking_objects.includes(item.preview_link.model)) {
                return '';
            }
            let redirect = {
                collection_orders: '/collection-orders/dashboard/',
                collectionorders: '/collection-bulks/dashboard/collection/',
                delivery_orders: '/delivery-orders/dashboard/',
                outbound_orders: '/outbound-orders/dashboard/',
                outsourced_orders: '/outsourced-orders/dashboard/',
                billing: '/billing/view/',
            };
            let href = `<a href="${item.preview_link.link}">&nbsp;${this.$__(innerText)}&nbsp;</a>`;
            if (item.preview_link.module in redirect) {
                href = `<a target="_blank" href="${redirect[item.module]}${item.preview_link.link}">&nbsp;${this.$__(innerText)}&nbsp;</a>`;
            }
            return href;
        },
    },
};

const trans_datatable = {
    data() {
        return {
            languageCdn: {
                nl: nl,
                de: de,
                cs: cs,
                pl: pl,
            },
        };
    },
    methods: {
        getLanguageTranslations(user_lang) {
            if (user_lang === 'en' || user_lang == '') {
                return {
                    search: '',
                    searchPlaceholder: 'Search...',
                };
            }
            return this.languageCdn[user_lang];
        },
    },
    computed: {
        ...mapState({
            lang: state =>
                state.auth && state.auth.user && state.auth.user.lang ? state.auth.user.lang : { code: 'en' },
        }),
    },
};

const fetchers = {
    data() {
        return {
            timeout: null,
            search_fields_json: null,
            search_filter: null,
        };
    },
    methods: {
        fetchRelations(search, loading) {
            let fields_json = this.search_fields_json;
            let search_filter = this.search_filter;
            let permission_scope = this.search_permission_scope;
            if (
                permission_scope === 'crm' &&
                typeof this.hasPermission === 'function' &&
                !this.hasPermission('crm_relations.see_all_relations') &&
                !this.hasPermission('crm_relations.see_own_relations')
            ) {
                this.relation_options = [];
                if (loading) {
                    loading(false);
                }
                return;
            }
            if (loading) {
                loading(true);
            }
            let self = this;
            if (search === undefined) {
                search = '';
            }
            clearTimeout(self.timeout);
            self.timeout = setTimeout(function () {
                agent.Relation.searchRelations(
                    { q: search },
                    { deleted: false },
                    { is_active: true },
                    permission_scope ? { permission_scope: permission_scope } : {},
                    { fields_json: fields_json ? fields_json : ['id', 'name', 'visiting_address_place', 'addresses'] },
                    { search_filter: search_filter }
                ).then(function (result) {
                    self.relation_options = result.data;
                    if (loading) {
                        loading(false);
                    }
                });
            }, 350);
        },
        fetchTransporters(search, loading) {
            if (loading) {
                loading(true);
            }
            let self = this;
            if (search === undefined) {
                search = '';
            }
            clearTimeout(self.timeout);
            self.timeout = setTimeout(function () {
                agent.Relation.searchRelations(
                    { q: search },
                    { deleted: false },
                    { is_active: true },
                    { is_transporter: true },
                    { fields_json: ['id', 'name', 'visiting_address_place', 'is_transporter'] }
                ).then(function (result) {
                    self.transporter_options = result.data;
                    if (loading) {
                        loading(false);
                    }
                });
            }, 350);
        },
        fetchStorageTypes(search, loading) {
            let self = this;
            if (loading) {
                loading(true);
            }
            if (search === undefined) {
                search = '';
            }
            agent.Storage.searchTypes(search).then(function (result) {
                self.type_options = result.data;
                if (loading) {
                    loading(false);
                }
            });
        },

        fetchWasteGroups(search, loading) {
            let self = this;
            this.debounceCallback(() => {
                this.debounceChecker(self.waste_groups_options, search, search => {
                    if (loading) {
                        loading(true);
                    }
                    if (search === undefined) {
                        search = '';
                    }
                    agent.Storage.searchWasteGroups(search).then(function (result) {
                        self.waste_groups_options = result.data;
                        if (loading) {
                            loading(false);
                        }
                    });
                });
            });
        },
        fetchEuralCodes(search, loading) {
            if (loading) {
                loading(true);
            }
            let self = this;
            if (search === undefined) {
                search = '';
            }
            clearTimeout(self.timeout);
            self.timeout = setTimeout(function () {
                agent.EuralCodes.searchEuralCodes(search).then(function (result) {
                    self.eural_code_options = result.data;
                    if (loading) {
                        loading(false);
                    }
                });
            }, 350);
        },
    },
};

const master_table_component = {
    mixins: [trans_datatable, utils],
    props: {
        actionButtonsWidth: {
            default: '',
            type: String,
        },
    },
    data() {
        return {
            datatable: null,
        };
    },
    methods: {
        getColumns() {
            this.table_columns = [];
            let self = this;
            let iterableColumnsNames;
            if (this.columns === '__all__') {
                iterableColumnsNames = Object.keys(this.whole_model_columns);
            } else {
                iterableColumnsNames = this.columns;
            }
            if (iterableColumnsNames === undefined) {
                console.log(
                    '%c' + `It is possible that you didn't specify columns for the table`,
                    'padding: 0 .5rem; background: crimson; font: 1.6em/1 Arial; color: white;'
                );
                return;
            }
            for (let columnName of iterableColumnsNames) {
                if (this.whole_model_columns[columnName]) {
                    this.table_columns.push({
                        data: function (row) {
                            return self.whole_model_columns[columnName].renderData.bind(null, row);
                        },
                        title: this.$__(this.whole_model_columns[columnName].htmlName),
                        name: this.whole_model_columns[columnName].name,
                        orderable: this.whole_model_columns[columnName].orderable,
                    });
                } else {
                    console.log(
                        '%c' + `${columnName} is not in the table component!!`,
                        'padding: 0 .5rem; background: crimson; font: 1.6em/1 Arial; color: white;'
                    );
                }
            }
        },
        getButtons() {
            let self = this;
            if (this.tableButtons && Array.isArray(this.tableButtons) && this.tableButtons.length > 0) {
                this.table_columns.push({
                    data: function (row) {
                        let actions = ``;
                        for (let button of self.tableButtons) {
                            if (self.available_buttons[button]) {
                                if (actions === '') {
                                    if (self.actionButtonsWidth) {
                                        actions += `<div class="action-buttons" style="width: ${self.actionButtonsWidth}">`;
                                    } else {
                                        actions += `<div class="action-buttons">`;
                                    }
                                }
                                actions += self.available_buttons[button](row);
                            }
                        }
                        if (actions !== '') {
                            actions += `</div>`;
                        }
                        return actions;
                    },
                    width: self.actionButtonsWidth,
                    title: self.$__('Actions'),
                });
            }
        },
        reloadTable(callback = null) {
            if (this.$refs?.tableRef && typeof this.$refs.tableRef.reload === 'function') {
                this.$refs.tableRef.reload(callback);
                return;
            }

            if (this.datatable && this.datatable.ajax && typeof this.datatable.ajax.reload === 'function') {
                if (callback) {
                    this.datatable.ajax.reload(callback);
                } else {
                    this.datatable.ajax.reload(null, false);
                }
                return;
            }

            if (callback) {
                callback();
            }
        },
        loadTable(setReady = true) {
            this.getColumns();
            this.getButtons();

            if (typeof this.loadDataTable === 'function') {
                this.loadDataTable();
                return;
            }

            if (setReady) {
                this.tableReady = true;
            }
        },
        truncateTable() {
            this.datatable.clear().destroy();
            $(this.$refs.tableRef).find('thead').html('');
            this.datatable = null;
        },
        checkSimilarity() {
            if (this.readyData || this.columns === '__all__') {
                return false;
            }
            let temp =
                this.table_columns.slice(-1)[0].title === __('Actions')
                    ? clone(this.table_columns.slice(0, -1))
                    : clone(this.table_columns);
            if (this.columns.length !== temp.length) {
                return false;
            }
            for (let index in temp) {
                if (temp[index].title !== this.whole_model_columns[this.columns[index]].htmlName) {
                    return false;
                }
            }
            return true;
        },
        triggerTable(callback = null) {
            let self = this;
            if (self.datatable === null) {
                self.loadTable();
            } else {
                if (self.queryData && self.notEmptyObject(self.queryData) && self.datatable.settings()[0].jqXHR) {
                    self.datatable.settings()[0].jqXHR.abort();
                }
                if (!self.checkSimilarity()) {
                    self.truncateTable();
                    self.loadTable();
                } else {
                    self.reloadTable(callback);
                }
            }
            $('input[type="search"]').each(function () {
                if ($(this).attr('aria-controls') && $(this).attr('aria-controls').includes('DataTables')) {
                    $(this).attr('autocomplete', 'off');
                    // Below brakes search functionality for DT, but removes autocomplete
                    // let label = $(this).parent()
                    // label.html(
                    //     `<form autocomplete="off" onsubmit="event.preventDefault()">${label.html()}</form>`
                    // )
                }
            });
        },
        addClickEventToComponent(selector, buttonName, func) {
            if (this.tableButtons && this.tableButtons.includes(buttonName)) {
                $(document).off('click', selector);
                $(document).on('click', selector, func);
            }
        },
        async emitRowUpdate(node, data) {
            await this.datatable.row(node).data(data);
        },
        getDataFromClickedRow(that) {
            /*
             * Call this function from parent to get data by this element from jquery event
             * */
            let self = this;
            let [node, data] = [null, null];
            if (!self.datatable || typeof self.datatable.row !== 'function') {
                return [node, data];
            }
            $(that)
                .parents()
                .each(function () {
                    if (this.tagName === 'TR') {
                        if (self.datatable.row(this).node()) {
                            node = self.datatable.row(this).node();
                            data = self.datatable.row(this).data();
                        }
                    }
                });
            return [node, data];
        },
        jumpToFirstPage() {
            setTimeout(() => {
                this.datatable.page('first').draw('page');
            }, 50);
        },
        switchToPage(pageNum, ajax = false) {
            setTimeout(() => {
                this.datatable.page(pageNum - 1);
                if (ajax) {
                    this.datatable.ajax.reload(null, false);
                } else {
                    this.datatable.draw(false);
                }
            }, 50);
        },
    },
    mounted() {
        let self = this;
        if (this.autoLoad) {
            setTimeout(() => {
                self.triggerTable();
            }, 100);
        }
        window.addEventListener('triggerTable', this.triggerTable);

        $(document).on('click', '.page-link', function (e) {
            self.$emit('pageChanged', parseInt($(this).html()));
        });

        $(document).on('change', '.dataTables_filter > label > input[type="search"]', function (e) {
            self.$emit('searchChanged', $(this).val());
        });
    },
    beforeUnmount() {
        window.removeEventListener('triggerTable', this.triggerTable);
        $(document).off('click', '.page-link');
        $(document).off('change', '.dataTables_filter > label > input[type="search"]');
    },
};

const appDatatableMixin = {
    data() {
        return {
            tableReady: false,
            table_fields: {},
            loadTableTimeoutId: null,
            isUnmounting: false,
        };
    },
    methods: {
        getColumns() {
            this.table_fields = {};
            let self = this;
            let iterableColumnsNames;
            if (this.columns === '__all__') {
                iterableColumnsNames = Object.keys(this.whole_model_columns);
            } else {
                iterableColumnsNames = this.columns;
            }
            if (iterableColumnsNames === undefined) {
                console.log(
                    '%c' + `It is possible that you didn't specify columns for the table`,
                    'padding: 0 .5rem; background: crimson; font: 1.6em/1 Arial; color: white;'
                );
                return;
            }
            for (let columnName of iterableColumnsNames) {
                const columnDefinition = this.whole_model_columns[columnName];
                if (columnDefinition) {
                    if (hasOwn(columnDefinition, 'visible') && columnDefinition.visible === false) {
                        continue;
                    }
                    this.table_fields[columnName] = {
                        label: this.$__(columnDefinition.label || columnDefinition.htmlName || columnName),
                        searchable: hasOwn(columnDefinition, 'searchable') ? columnDefinition.searchable : undefined,
                        sortable: hasOwn(columnDefinition, 'sortable')
                            ? columnDefinition.sortable
                            : columnDefinition.orderable,
                        name: columnDefinition.name,
                        sName: columnDefinition.sName,
                        data: function (row) {
                            const renderer = columnDefinition.data || columnDefinition.renderData;
                            if (typeof renderer === 'function') {
                                return renderer.bind(null, row);
                            }
                            return row[columnName];
                        },
                        visible: columnDefinition.visible,
                        width: columnDefinition.width,
                        className: columnDefinition.className,
                        defaultOrder: columnDefinition.defaultOrder || columnDefinition.defaultSort,
                        template: columnDefinition.template,
                        isLocal: columnDefinition.isLocal,
                        index: columnDefinition.index,
                        lookup: columnDefinition.lookup,
                        type: columnDefinition.type,
                    };
                } else {
                    console.log(
                        '%c' + `${columnName} is not in the table component!!`,
                        'padding: 0 .5rem; background: crimson; font: 1.6em/1 Arial; color: white;'
                    );
                }
            }
        },
        getButtons() {
            let self = this;
            if (this.tableButtons && Array.isArray(this.tableButtons) && this.tableButtons.length > 0) {
                this.table_fields['actions'] = {
                    data: function (row) {
                        let actions = ``;
                        for (let button of self.tableButtons) {
                            if (self.available_buttons[button]) {
                                if (actions === '') {
                                    if (self.actionButtonsWidth) {
                                        actions += `<div class="action-buttons" style="width: ${self.actionButtonsWidth}">`;
                                    } else {
                                        actions += `<div class="action-buttons">`;
                                    }
                                }
                                actions += self.available_buttons[button](row);
                            }
                        }
                        if (actions !== '') {
                            actions += `</div>`;
                        }
                        return actions;
                    },
                    width: self.actionButtonsWidth,
                    label: self.$__('Actions'),
                    isLocal: true,
                    className: 'dt-center',
                };
            }
        },
        loadTable(setReady = true) {
            if (this.tableReady) {
                this.datatable.reload();
                return;
            }
            if (this.loadTableTimeoutId) {
                clearTimeout(this.loadTableTimeoutId);
                this.loadTableTimeoutId = null;
            }
            return new Promise((resolve, reject) => {
                this.getColumns();
                this.getButtons();
                if (setReady) {
                    this.loadTableTimeoutId = setTimeout(() => {
                        this.loadTableTimeoutId = null;
                        if (this.isUnmounting) {
                            resolve();
                            return;
                        }
                        this.tableReady = true;
                        this.$nextTick(() => {
                            resolve();
                        });
                    }, 100);
                } else {
                    resolve();
                }
            });
        },
        reloadTable(callback = null) {
            if (this.datatable && typeof this.datatable.reload === 'function') {
                this.datatable.reload(callback);
                return;
            }

            if (callback) {
                callback();
            }
        },
        triggerTable(callback = null) {
            if (!this.tableReady) {
                const result = this.loadTable();
                if (result && typeof result.then === 'function') {
                    result.then(() => {
                        if (callback) {
                            callback();
                        }
                    });
                } else if (callback) {
                    callback();
                }
                return;
            }

            this.reloadTable(callback);
        },
        truncateTable() {
            if (this.datatable && typeof this.datatable.reinit === 'function') {
                this.datatable.reinit();
            }
        },
        jumpToFirstPage(api) {
            setTimeout(() => {
                api.page('first').draw('page');
            }, 50);
        },
        switchToPage(pageNum, ajax = false) {
            if (!this.datatable?.api) {
                return;
            }

            setTimeout(() => {
                this.datatable.api.page(pageNum - 1);
                if (ajax && this.datatable.api.ajax && typeof this.datatable.api.ajax.reload === 'function') {
                    this.datatable.api.ajax.reload(null, false);
                } else {
                    this.datatable.api.draw(false);
                }
            }, 50);
        },
        async emitRowUpdate(node, data) {
            if (!this.datatable?.api) {
                return;
            }

            this.datatable.api.row(node).data(data);
            this.datatable.api.draw(false);
        },
        addClickEventToComponent(selector, buttonName, func) {
            if (this.tableButtons && this.tableButtons.includes(buttonName)) {
                $(document).off('click', selector);
                $(document).on('click', selector, func);
            }
        },
        getDataFromClickedRow(that) {
            /*
             * Call this function from parent to get data by this element from jquery event
             * */
            let self = this;
            let [node, data] = [null, null];
            const datatable = self.$refs?.tableRef || self.datatable;
            const datatableApi = datatable?.api || null;
            if (!datatableApi || typeof datatableApi.row !== 'function') {
                return [node, data];
            }
            $(that)
                .parents()
                .each(function () {
                    if (this.tagName === 'TR') {
                        const row = datatableApi.row(this);
                        if (row.node()) {
                            node = row.node();
                            data = row.data();
                        }
                    }
                });
            return [node, data];
        },
    },
    computed: {
        datatable() {
            return this.$refs['tableRef'];
        },
        datatableFieldsFiltered() {
            if (this.fields && Array.isArray(this.fields) && this.datatableFields) {
                return this.fields.reduce((acc, field) => {
                    if (this.datatableFields[field]) {
                        acc[field] = this.datatableFields[field];
                    }
                    return acc;
                }, {});
            }
            return this.datatableFields;
        },
    },
    beforeUnmount() {
        this.isUnmounting = true;
        if (this.loadTableTimeoutId) {
            clearTimeout(this.loadTableTimeoutId);
            this.loadTableTimeoutId = null;
        }
    },
};

const nested_objects_mixin = {
    methods: {
        removeNestedObjects(obj, attribute) {
            let obj_copy = cloneDeep(obj);
            for (let [key, value] of Object.entries(obj_copy)) {
                if (value != null && typeof value === 'object' && hasOwn(value, attribute)) {
                    obj_copy[key] = value[attribute];
                    this.renameObjectField(obj_copy, `${key}_${attribute}`, key);
                } else if (Array.isArray(value)) {
                    value.forEach((obj_value, index) => {
                        if (obj_value != null && typeof obj_value === 'object' && hasOwn(obj_value, attribute)) {
                            value[index] = obj_value[attribute];
                        }
                    });
                    this.renameObjectField(obj_copy, `${key}_${attribute}`, key);
                }
            }
            return obj_copy;
        },
        renameObjectField(o, newKey, oldKey) {
            delete Object.assign(o, { [newKey]: o[oldKey] })[oldKey];
        },
    },
};

const array_mixin = {
    mounted() {
        Array.isNotEmpty = function (array) {
            return Array.isArray(array) && array.length;
        };
        Array.isEmpty = function (array) {
            return Array.isArray(array) && !array.length;
        };
    },
};

const watcher_mixin = {
    methods: {
        createWatcher(watcher_name, watch_func, args) {
            return this.$watch(
                () => {
                    return Object.byString(this, watcher_name);
                },
                (val, val2) => {
                    watch_func(val, val2);
                },
                args
            );
        },
        unwatchWatchers(watchers) {
            for (let watch of watchers) {
                watch();
            }
            watchers = [];
        },
    },
    mounted() {
        Object.byString = function (object, string) {
            /**
             * @param {Object} object Object.
             * @param {String} string String.
             *
             * @return {any} Returns object's property by given string.
             *
             * @example Object.byString(object, "prop1.prop2[0].prop3") => object.prop1.prop2[0].prop3
             */
            string = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
            string = string.replace(/^\./, ''); // strip a leading dot
            let a = string.split('.');
            for (let i = 0, n = a.length; i < n; ++i) {
                let k = a[i];
                if (object && k in object) {
                    object = object[k];
                } else {
                    return;
                }
            }
            return object;
        };
    },
};

const card_mixin = {
    mixins: [watcher_mixin],
    props: {
        is_preview: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            card_content: {},
            inputErrors: {},
            requiredFields: [],
        };
    },
    methods: {
        setCardContent(prefill_data) {
            for (let [prefill_field_key, prefill_field_value] of Object.entries(prefill_data)) {
                if (hasOwn(this.card_content, prefill_field_key)) {
                    this.card_content[prefill_field_key] = prefill_field_value;
                }
            }
        },
        getCardContent() {
            return this.card_content;
        },
        validateFields() {
            let has_error = false;
            this.inputErrors = {};
            return !has_error;
        },
    },
};

const vmodel_card_mixin = {
    emits: ['input', 'update:modelValue'],
    props: {
        modelValue: {},
        value: {},
    },
    watch: {
        card_content: {
            deep: true,
            handler(newVal, oldVal) {
                this.$emit('input', this.card_content);
                this.$emit('update:modelValue', this.card_content);
            },
        },
        modelValue: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal !== undefined) {
                    this.card_content = newVal;
                }
            },
        },
        value: {
            deep: true,
            handler(newVal, oldVal) {
                if (this.modelValue === undefined) {
                    this.card_content = newVal;
                }
            },
        },
    },
    data() {
        return {
            card_content: this.modelValue !== undefined ? this.modelValue : this.value,
        };
    },
};

const tab_mixin = {
    props: {
        is_preview: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    methods: {
        setTabContent(prefill_data) {
            for (let [card_name, card_component] of Object.entries(this.$refs)) {
                if (typeof card_component?.setCardContent === 'function') {
                    card_component.setCardContent(prefill_data);
                }
            }
        },
        collectDataFromCards() {
            let data_from_cards = clone(this.tab_content);
            for (let [card_name, card_component] of Object.entries(this.$refs)) {
                if (
                    card_component &&
                    card_name.slice(card_name.length - 5) === '_card' &&
                    typeof card_component.getCardContent === 'function'
                ) {
                    _.extend(data_from_cards, card_component.getCardContent());
                }
            }
            return data_from_cards;
        },
        validateFieldsFromCards() {
            let doesnt_have_error = true;
            for (let [card_name, card_component] of Object.entries(this.$refs)) {
                if (
                    card_name.slice(card_name.length - 5) === '_card' &&
                    card_component &&
                    typeof card_component.validateFields === 'function'
                ) {
                    doesnt_have_error &= card_component.validateFields();
                }
            }
            return doesnt_have_error;
        },
    },
};

const hash = {
    data() {
        return {};
    },
    methods: {
        remove_hash(event = null) {
            if (event?.detail?.onlyLast) {
                const fullPath = this.$route.fullPath;
                const hashIndex = fullPath.lastIndexOf('#');
                if (hashIndex > -1) {
                    this.skipNextHashCatcher = !!event.detail.keepModals;
                    this.$router.replace(fullPath.slice(0, hashIndex));
                }
                return;
            }
            this.$router.replace(this.$route.fullPath.split('#')[0]);
        },
        parseHashParams(str) {
            let hashes = str.split('#').filter(h => h !== '');
            let dataArr = [];
            for (let hash of hashes) {
                let pieces = hash.split('&'),
                    data = {},
                    i,
                    parts;
                for (i = 0; i < pieces.length; i++) {
                    parts = pieces[i].split('=');
                    if (parts.length < 2) {
                        parts.push('');
                    }
                    data[decodeURIComponent(parts[0].replace('#', ''))] = decodeURIComponent(parts[1]);
                }
                dataArr.push(data);
            }
            return dataArr;
        },
    },
};

const modal_worker = {
    methods: {
        notEmptyObject(obj) {
            return obj !== null && obj ? Object.keys(obj).length > 0 : false;
        },
        closeDefinedModals(array = null, callback = null) {
            /**
             Takes names of refs as argument type=Array, if nothing sent, then works for all refs
             **/
            if (!this.notEmptyObject(this.$refs)) {
                return;
            }
            let modals = {
                profile: 'edit_profile_modal',
                notification: 'notification_view',
                employee: 'employee_view_modal',
                task: 'task_modal',
                sale: 'sale_view',
                contact: 'contact_view',
                relation: 'relation_view_modal',
                user: 'user_view_modal',
                transport: 'transport_unit_modal',
                storage: 'storage_unit_view_modal',
                address: 'address_view_modal',
                disposalAddress: 'address-disposal-history-modal',
            };
            if (!array) {
                array = Object.keys(modals);
            }
            for (let ref of array) {
                if (modals[ref]) {
                    const modalRef = this.$refs[modals[ref]];
                    if (modalRef && 'modalShow' in modalRef) {
                        modalRef.modalShow = false;
                    }
                }
            }
            if (callback) {
                this.$nextTick(callback);
            }
        },
    },
    mounted() {},
};

const bv_modal_utils = {
    mixins: [hash, modal_worker],
    data() {
        return {
            route: '',
            hashDispatchId: 0,
            skipNextHashCatcher: false,
        };
    },
    methods: {
        createEventForModalOpen(data) {
            const id = data.view;
            const model = data.model.split('-')[1];
            let extra = null;
            let general = ['view', 'model'];
            for (let ex of Object.keys(data)) {
                if (!general.includes(ex)) {
                    if (extra === null) {
                        extra = {};
                    }
                    extra[ex] = data[ex];
                }
            }
            return new CustomEvent(`set${model.charAt(0).toUpperCase() + model.slice(1)}View`, {
                detail: {
                    id: id,
                    extra: extra,
                },
            });
        },
        extendEvent(event, data) {
            if (event) {
                for (let [_key, _value] of Object.entries(data)) {
                    event.detail[_key] = _value;
                }
            }
            return event;
        },
        async dispatchEvt(event, runId) {
            if (!(event instanceof Event)) {
                return false;
            }
            await this.$nextTick();
            if (runId !== this.hashDispatchId || event.detail.__hash !== this.$route.hash) {
                return false;
            }
            window.dispatchEvent(event);
            return true;
        },
        async hashCatcher() {
            const runId = ++this.hashDispatchId;
            const routeHash = this.$route.hash;
            this.closeDefinedModals();

            if (!routeHash) {
                return;
            }
            if (!routeHash.includes('model=')) {
                return;
            }

            const dataArr = this.parseHashParams(routeHash);
            if (typeof this.ensureGlobalModalsForHash === 'function') {
                await this.ensureGlobalModalsForHash(dataArr);
            }

            if (runId !== this.hashDispatchId || routeHash !== this.$route.hash) {
                return;
            }

            let currentEvent = null;
            for (let data of dataArr) {
                if ('view' in data && 'model' in data) {
                    if (currentEvent) {
                        const dispatched = await this.dispatchEvt(currentEvent, runId);
                        if (!dispatched) {
                            return;
                        }
                    }
                    currentEvent = this.createEventForModalOpen(data);
                    currentEvent.detail.__hash = routeHash;
                } else if ('tab' in data && currentEvent) {
                    this.extendEvent(currentEvent, data);
                }
            }

            if (currentEvent) {
                await this.dispatchEvt(currentEvent, runId);
            }
        },
    },
    beforeUnmount() {
        window.removeEventListener('remove_hash', this.remove_hash);
    },
    mounted() {
        let self = this;
        window.addEventListener('remove_hash', this.remove_hash);
        self.$nextTick(() => {
            if (this.$route.hash && this.$route.hash.includes('model=')) {
                this.hashCatcher();
            }
            this.$watch(
                '$route',
                (to, from) => {
                    if (to.hash !== from.hash) {
                        if (this.skipNextHashCatcher) {
                            this.skipNextHashCatcher = false;
                            return;
                        }
                        this.hashCatcher();
                    }
                },
                { deep: true }
            );
        });
    },
};

const vmodel_tab_mixin = {
    emits: ['input', 'update:modelValue'],
    props: {
        modelValue: {},
        value: {},
    },
    watch: {
        tab_content: {
            deep: true,
            handler(newVal, oldVal) {
                this.$emit('input', this.tab_content);
                this.$emit('update:modelValue', this.tab_content);
            },
        },
        modelValue: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal !== undefined) {
                    this.tab_content = newVal;
                }
            },
        },
        value: {
            deep: true,
            handler(newVal, oldVal) {
                if (this.modelValue === undefined) {
                    this.tab_content = newVal;
                }
            },
        },
    },
    data() {
        return {
            tab_content: this.modelValue !== undefined ? this.modelValue : this.value,
        };
    },
};

const switch_tab_mixin = {
    data() {
        return {
            is_active: false,
        };
    },
    methods: {
        switchTab() {
            this.switchTabState();
        },
        switchTabState() {
            this.is_active = !this.is_active;
        },
    },
};

const switch_tabs_mixin = {
    data() {
        return {
            first_active_tab_name: '', // Should be the same as ref and ends with '_tab'
        };
    },
    methods: {
        initTab(tab_name) {
            if (!this.isActiveTab(tab_name)) {
                this.switchTabs(tab_name);
            }
        },
        isActiveTab(tab_name) {
            if (this.tabExistsInRefs(tab_name) && 'is_active' in this.$refs[tab_name]) {
                return this.$refs[tab_name].is_active;
            }
            return false;
        },
        switchTabs(tab_name) {
            for (let [ref_tab_name, tab_component] of Object.entries(this.$refs)) {
                if (!ref_tab_name.endsWith('_tab') || !tab_component) {
                    continue;
                }
                if (ref_tab_name == tab_name && typeof tab_component.switchTab === 'function') {
                    tab_component.is_active = true;
                } else if ('is_active' in tab_component) {
                    tab_component.is_active = false;
                }
            }
        },
        tabExistsInRefs(tab_name) {
            return Object.prototype.hasOwnProperty.call(this.$refs, tab_name) && tab_name.endsWith('_tab');
        },
        activateFirstActiveTab(tab_name) {
            if (!this.isActiveTab(tab_name)) {
                try {
                    if (typeof this.$refs[tab_name]?.switchTab === 'function') {
                        this.$refs[tab_name].switchTab();
                    } else {
                        console.error(`Tab '${tab_name}' doesn\`t have 'switchTab' method`);
                    }
                } catch (ex) {}
            }
        },
    },
    mounted() {
        if (this.first_active_tab_name) {
            this.$nextTick(() => {
                this.activateFirstActiveTab(this.first_active_tab_name);
            });
        }
    },
};

const get_params_mixin = {
    methods: {
        findGetParameter(parameterName) {
            let qd = {};
            if (location.search) {
                location.search
                    .substr(1)
                    .split('&')
                    .forEach(function (item) {
                        let s = item.split('=');
                        let k = s[0];
                        let v = s[1] && decodeURIComponent(s[1]);
                        if (v) {
                            qd[k] = v.split(',');
                        }
                    });
            }
            if (hasOwn(qd, parameterName)) {
                return qd[parameterName];
            }
            return [];
        },
    },
};

const coerce_props_mixin = {
    /**
     * @example props: {
     *     property: {
     *         type: String,
     *         coerce: property => property.trim(),
     *     }
     * },
     */
    beforeCreate() {
        const { props } = this.$options;
        if (!props) return;

        this._$coertions = Object.keys(props)
            .filter(k => props[k].coerce)
            .map(k => [k, props[k].coerce]);
    },
    computed: {
        $coerced() {
            return this._$coertions.reduce((c, [k, coerce]) => {
                c[k] = coerce.call(this, this.$props[k]);
                return c;
            }, {});
        },
    },
};

const string_mixin = {
    mounted() {
        String.prototype.capitalize = function () {
            return this.charAt(0).toUpperCase() + this.slice(1);
        };
    },
};

const b_modal_mixin = {
    data() {
        return {
            modalShow: false,
        };
    },
    methods: {
        showModal() {
            this.modalShow = true;
        },
        hideModal() {
            this.modalShow = false;
        },
    },
};

const datatable_formatting_mixin = {
    methods: {
        datatableFormatDateTime(datetime, showTime = true) {
            let splitted = datetime.split('T');
            let time = null;
            if (splitted.length > 1) {
                time = splitted[1];
            }
            let date = splitted[0];
            let d = moment(date);
            if (time && showTime) {
                d = moment(date + ' ' + time);
            }
            return `
                ${d.format('DD MMM YYYY')}
                <small class="text-nowrap me-1">${d.hour() || d.minute() ? '- ' + d.format('HH:mm') : ''}</small>
            `;
        },
    },
};

const datatable_mixin = {
    watch: {
        lang: function (newVal, oldVal) {
            if ((newVal && !oldVal) || (newVal && oldVal && newVal.code !== oldVal.code)) {
                if (this.datatable) {
                    this.datatable.destroy();
                    this.drawTable();
                }
            }
        },
    },
    methods: {
        initializeTable() {
            if (!this.datatable) {
                this.drawTable();
            } else {
                this.redrawTable();
            }
        },
        drawTable() {
            console.warn('To draw table overwrite drawTable() method!');
        },
        redrawTable() {
            this.datatable.ajax.reload(utils.methods.setFocusOnSearchField);
        },
    },
    computed: {
        ...mapState({
            lang: state =>
                state.auth && state.auth.user && state.auth.user.lang ? state.auth.user.lang : { code: 'en' },
        }),
    },
};

const SORT_ORDER = {
    ASCENDING: 1,
    DESCENDING: -1,
    NONE: 0,
};

const custom_table_sorting_mixin = {
    data() {
        return {
            SORT_ORDER: SORT_ORDER,
            sort_by: 'id',
            sort_by_options: ['id'],
            sort_order: SORT_ORDER.ASCENDING,
            sort_order_options: [SORT_ORDER.ASCENDING, SORT_ORDER.DESCENDING, SORT_ORDER.NONE],
        };
    },
    computed: {
        sorted_table_data() {
            if (!this.sort_by_options.includes(this.sort_by)) {
                console.warn(`Cannot be sorted by '${this.sort_by}'!`);
                return this.card_content.table_data;
            }
            if (!this.sort_order_options.includes(this.sort_order)) {
                console.warn(`Cannot be sorted by '${this.sort_order}' order!`);
                return this.card_content.table_data;
            }
            if (this.sort_order === this.SORT_ORDER.NONE) {
                return this.card_content.table_data;
            }
            return cloneDeep(this.card_content.table_data).sort((a, b) => {
                if (hasOwn(a, this.sort_by) && hasOwn(b, this.sort_by)) {
                    return (
                        (a[this.sort_by] < b[this.sort_by] ? -1 : a[this.sort_by] > b[this.sort_by] ? 1 : 0) *
                        this.sort_order
                    );
                } else {
                    console.warn(
                        `Cannot be sorted by '${this.sort_by}'! Not all objects in table_data have '${this.sort_by}' property.`
                    );
                }
            });
        },
    },
    methods: {
        sortBy(sort_by) {
            if (this.sort_by === sort_by) {
                let sort_change_order = {
                    [this.SORT_ORDER.NONE]: this.SORT_ORDER.ASCENDING,
                    [this.SORT_ORDER.ASCENDING]: this.SORT_ORDER.DESCENDING,
                    [this.SORT_ORDER.DESCENDING]: this.SORT_ORDER.NONE,
                };
                this.sort_order = sort_change_order[this.sort_order];
            } else {
                this.sort_by = sort_by;
                this.sort_order = this.SORT_ORDER.ASCENDING;
            }
        },
    },
};

const filter_remember_mixin = {
    mixins: [utils],
    methods: {
        setFiltersFromUserPreferences(customPath = null) {
            let path = customPath ? customPath : this.filtering;
            if (
                !this.user.preferences ||
                !this.user.preferences.filtering ||
                typeof this.user.preferences.filtering !== 'object' ||
                this.user.preferences.filtering === null
            ) {
                this.user.preferences.filtering = {};
            }
            if (this.user.preferences.filtering[path.filterName]) {
                path.rememberButton = this.user.preferences.filtering[path.filterName].rememberButton;
            }
            if (
                this.user.preferences.filtering[path.filterName] &&
                this.user.preferences.filtering[path.filterName].filters
            ) {
                path.filters = this.user.preferences.filtering[path.filterName].filters;
            }
        },

        updateUserFilteringPreference(customPath = null) {
            let path = customPath ? customPath : this.filtering;
            if (!path || !path.filterName) {
                return this.updateUser({
                    id: this.user.id,
                    preferences: this.user.preferences,
                    updType: 'partial',
                });
            }
            if (!this.user.preferences.filtering || !this.user.preferences.filtering[path.filterName]) {
                this.user.preferences.filtering = this.user.preferences.filtering
                    ? this.user.preferences.filtering
                    : {};
                this.user.preferences.filtering[path.filterName] = {
                    rememberButton: true,
                    filters: null,
                };
            }
            this.user.preferences.filtering[path.filterName].rememberButton = path.rememberButton;
            if (path.rememberButton) {
                this.user.preferences.filtering[path.filterName].filters = path.filters;
            } else {
                this.user.preferences.filtering[path.filterName].filters = null;
            }
            let userObject = {
                id: this.user.id,
                preferences: this.user.preferences,
                updType: 'partial',
            };
            return this.updateUser(userObject);
        },

        openPrefilledShortcutModal(name = '', filters = {}) {
            let filtLink = this.obj2base64(filters);
            let link = `${current_scheme}://${current_site}${this.$route.path}?set-filters=${filtLink}`;
            window.dispatchEvent(new CustomEvent('openPrefilledShortcut', { detail: { name, link } }));
        },

        setupFilters(filtersPath, filteringPath = null) {
            /*
             * This function will handle the way filters to be loaded
             * First argument is responsible for receiving the correct path where `filters` object is
             * Second argument expects to receive an object where `filtering` object is, it's optional and by default set to this.filtering
             * */
            if ('set-filters' in this.$route.query) {
                filtersPath.filters = { ...filtersPath.filters, ...this.base642obj(this.$route.query['set-filters']) };
                if (hasOwn(this, 'triggerFiltrate')) {
                    this.triggerFiltrate();
                }
            } else {
                this.setFiltersFromUserPreferences(filteringPath);
            }
        },
    },
};

const shared_filters_mixin = {
    mounted() {
        document.addEventListener('keypress', this.handleKeyDown);
        Array.from(document.getElementsByClassName('fixed-parent')).forEach(el =>
            el.style.setProperty('height', this.vhToPixels(100) + 'px')
        );
        Array.from(document.getElementsByClassName('overlay')).forEach(el =>
            el.style.setProperty('max-height', this.vhToPixels(80) + 'px')
        );
        // set max-height for nav_content to 80vh - 200px
        Array.from(document.getElementsByClassName('nav_content')).forEach(el =>
            el.style.setProperty('max-height', this.vhToPixels(80) - 100 + 'px')
        );
    },
    beforeUnmount() {
        document.removeEventListener('keypress', this.handleKeyDown);
    },
    methods: {
        blurSelect() {
            setTimeout(() => {
                document.querySelectorAll('.vue-select-filters-normalization input').forEach(el => el.blur());
            }, 0);
        },
        handleKeyDown(event) {
            if (!this.show_nav) {
                return;
            }
            if (event.keyCode === 13) {
                // 13 is the keycode for Enter key
                this.filterButton();
            }
        },
        vhToPixels(vh) {
            return Math.round(window.innerHeight / (100 / vh));
        },
        resetMessages() {
            for (const key in this) {
                // If the property name ends with '_alert', set its value to ''
                if (key.endsWith('_alert')) {
                    this[key] = '';
                }
            }
            this.filtersApplied = false;
        },
    },
};

const datepicker_extension_mixin = {
    methods: {
        convertNumToFullDate(date) {
            let lang_code = this.user && this.user.lang ? this.user.lang.code : this.lang.code;
            if (!date) {
                return null;
            }
            if (Array.isArray(date)) {
                let dates = [];
                for (let _date of date) {
                    if (_date) {
                        let formatted = moment(_date, moment.HTML5_FMT.DATE, lang_code).format('dddd D MMMM YYYY');
                        if (formatted !== 'Invalid date') {
                            dates.push(formatted);
                        } else {
                            dates.push(null);
                        }
                    } else {
                        dates.push(null);
                    }
                }
                return dates;
            }
            return moment(date, moment.HTML5_FMT.DATE, lang_code).format('dddd D MMMM YYYY');
        },
        convertFullDateNameToNum(date) {
            let lang_code = this.user && this.user.lang ? this.user.lang.code : this.lang.code;
            if (!date) {
                return null;
            }
            if (Array.isArray(date)) {
                let dates = [];
                for (let _date of date) {
                    if (_date) {
                        let formatted = moment(_date, 'dddd D MMMM YYYY', lang_code).format(moment.HTML5_FMT.DATE);
                        if (formatted !== 'Invalid date') {
                            dates.push(formatted);
                        } else {
                            dates.push(null);
                        }
                    } else {
                        dates.push(null);
                    }
                }
                return dates;
            }
            return moment(date, 'dddd D MMMM YYYY', lang_code).format(moment.HTML5_FMT.DATE);
        },
    },
};

const roles_mixin = {
    data() {
        return {
            roles: enum_user_roles,
        };
    },
};

const resource_socket = {
    data() {
        return {
            resource_socket: null,
            resource_socket_locket_registry: [],
        };
    },
    methods: {
        getLockedResources() {
            try {
                let data = {
                    list_resources: true,
                };
                this.resource_socket.send(JSON.stringify(data));
            } catch (DOMException) {
                return false;
            }
        },
        lockResource(module, instance_id, kwargs) {
            let data = {
                module: module,
                user_id: this.user.id,
                instance_id: instance_id,
                payload: kwargs,
                lock_resource: true,
            };
            setTimeout(() => {
                this.resource_socket.send(JSON.stringify(data));
            }, 1000);
        },
        unlockResource(module, instance_id) {
            let data = {
                module: module,
                instance_id: instance_id,
                unlock_resource: true,
            };
            this.resource_socket.send(JSON.stringify(data));
        },
        handleLockedResources() {
            /*
             * handleLockedResources function should be implemented
             * based on needed behaviour inside the component
             * */
        },
        allowResourceOpen(module, instance_id) {
            return new Promise((resolve, reject) => {
                this.getLockedResources();
                this.$nextTick(() => {
                    if (this.resource_socket_locket_registry.length > 0) {
                        resolve(
                            this.resource_socket_locket_registry.filter(r => {
                                return r.module === module && parseInt(r.instance_id) === parseInt(instance_id);
                            }).length === 0
                        );
                    }
                    resolve(true);
                });
            });
        },
    },
    created() {
        let self = this;
        let loc = window.location;
        let wsStart = 'ws://';
        if (loc.protocol == 'https:') {
            wsStart = 'wss://';
        }
        this.resource_socket = new ReconnectingWebSocket(wsStart + loc.host + '/ws/locked-resources/', undefined, {
            maxRetries: 10,
        });

        this.resource_socket.onopen = function (e) {
            self.getLockedResources();
        };
        this.resource_socket.onmessage = function (e) {
            let data = JSON.parse(e.data);
            if ('locked_resources' in data) {
                self.resource_socket_locket_registry = data['locked_resources'];
                self.handleLockedResources();
                return;
            }
            self.handleLockedResources(data);
        };
    },
    beforeUnmount() {
        if (this.resource_socket) {
            this.resource_socket.onopen = function () {};
            this.resource_socket.onmessage = function () {};
            this.resource_socket.onerror = function () {};
            this.resource_socket.onclose = function () {};
            this.resource_socket.close();
            this.resource_socket = null;
        }
    },
    computed: {
        ...mapState({
            user: state => (state.auth && state.auth.user ? state.auth.user : null),
        }),
    },
};

const ultimateDragMixin = {
    data() {
        return {
            selectorClass: null,
            startCoordinates: {
                top: 0,
                left: 0,
            },
            dragDelta: {
                top: 0,
                left: 0,
            },
        };
    },
    watch: {
        selectorClass: function (newVal, oldVal) {
            if (!newVal || oldVal !== newVal) {
                if (oldVal) {
                    this.destructDraggableEvents(oldVal);
                }
            }
        },
    },
    methods: {
        setDraggableSelector(sel) {
            this.selectorClass = sel;
            this.bindEvent();
        },
        bindEvent() {
            let self = this;
            $(document).off('mousedown', this.selectorClass);
            $(document).off('mouseup', this.selectorClass);
            $(this.selectorClass).addClass('cursor-pointer');
            $(document).on('mousedown', this.selectorClass, function (e) {
                self.startCoordinates = {
                    top: e.clientY,
                    left: e.clientX,
                };
                $(document).on('mousemove', function (e) {
                    self.dragDelta = {
                        top: e.clientY - self.startCoordinates.top,
                        left: e.clientX - self.startCoordinates.left,
                    };
                });
            });
            $(document).on('mouseup', this.selectorClass, function () {
                self.killMouseMoveEvent();
            });
        },
        killMouseMoveEvent(lookup = this.selectorClass) {
            $(document).off('mousemove');
            this.applyMove();
            this.dragDelta = {
                top: 0,
                left: 0,
            };
        },
        destructDraggableEvents(lookup = this.selectorClass) {
            $(document).off('mousedown', lookup);
            this.killMouseMoveEvent(lookup);
            $(document).off('mouseup', lookup);
        },
    },
    beforeUnmount() {
        this.destructDraggableEvents();
    },
};

const user_mixin = {
    mixins: [roles_mixin],
    computed: {
        ...mapState({
            user: state => (state.auth && state.auth.user ? state.auth.user : null),
        }),
        isExternalDepot() {
            return Boolean(
                this.user &&
                this.user.roles &&
                this.user.roles.length == 1 &&
                this.user.roles.some(r => r.id === this.roles.DEPO_EXTERNAL.id)
            );
        },
    },
};

const get_socket_mixin = {
    computed: {
        ...mapGetters({
            socket: 'socket/socket',
        }),
        socket_message() {
            return this.socket.message;
        },
    },
};

const price_list_mixin = {
    methods: {
        checkRevisionIsActiveNow(revision) {
            revision = cloneDeep(revision);
            if (revision.deactivated) {
                return false;
            }
            let start_date = moment(revision.start_date, 'YYYY-MM-DD');
            let end_date = revision.end_date ? moment(revision.end_date, 'YYYY-MM-DD') : null;
            let today = moment();
            if (start_date.isSameOrBefore(today)) {
                if (!end_date) {
                    return true;
                }
                if (end_date.isSameOrAfter(today)) {
                    return true;
                }
            }
            return false;
        },
        findAllActiveRevisions(revisions, _sort = false, mutable = false) {
            if (!mutable) {
                revisions = cloneDeep(revisions);
            }
            let active_revisions = [];
            for (let revision of revisions) {
                if (this.checkRevisionIsActiveNow(revision)) {
                    active_revisions.push(revision);
                }
            }
            if (_sort === 'by_date') {
                active_revisions.sort((a, b) =>
                    moment(a.start_date, 'YYYY-MM-DD').isAfter(moment(b.start_date, 'YYYY-MM-DD'))
                        ? -1
                        : moment(b.start_date, 'YYYY-MM-DD').isAfter(moment(a.start_date, 'YYYY-MM-DD'))
                          ? 1
                          : 0
                );
            }
            if (_sort === 'by_id') {
                active_revisions.sort((a, b) => (a.id > b.id ? -1 : b.id > a.id ? 1 : 0));
            }
            return active_revisions;
        },
        findLastRevision(revisions, mutable = false) {
            if (!mutable) {
                revisions = cloneDeep(revisions);
            }
            let latestId = 0;
            for (let index in revisions) {
                if (revisions[index].id > latestId) {
                    latestId = index;
                }
            }
            return revisions[latestId];
        },
        getItemsOutOfConfigurations(revision, mutable = true) {
            /*
             * Returns new revision where items added from price_configurations
             * */
            if (!mutable) {
                revision = cloneDeep(revision);
            }
            for (let config of revision.price_configurations) {
                if (!revision.items) {
                    revision.items = [];
                }
                revision.items.push(config.item);
            }
            revision.items = this.uniqueArray(revision.items);
            for (let item of revision.items) {
                item.price_configurations = revision.price_configurations.filter(c => c.item.id === item.id);
            }
            return revision;
        },
    },
};

const getWasteGroupsMixin = {
    data() {
        return {
            wasteGroupOptions: [],
            fields_json: '',
        };
    },
    mounted() {
        if (!this.is_preview) {
            this.fetchWasteGroupOptions();
        }
    },
    methods: {
        fetchWasteGroupOptions(search, loading) {
            this.debounceCallback(() => {
                if (loading) {
                    loading(true);
                }
                if (search === undefined) {
                    search = '';
                }
                if (!search.length) {
                    if (!this.waste_groups_object.requested) {
                        this.$store
                            .dispatch('orders/getAllWasteGroups', {
                                search: '',
                                fields: this.fields_json,
                            })
                            .then(() => {
                                this.wasteGroupOptions = this.cloneObj(this.waste_groups);
                            });
                    } else {
                        this.wasteGroupOptions = this.cloneObj(this.waste_groups);
                    }
                } else if (search.length) {
                    agent.CollectionWasteGroup.search(search, this.fields_json).then(response => {
                        this.wasteGroupOptions = response.data;
                        if (loading) {
                            loading(false);
                        }
                    });
                }
            });
        },
    },
    computed: {
        ...mapGetters({
            waste_groups_object: 'orders/waste_groups_object',
        }),
        waste_groups() {
            return this.waste_groups_object.waste_groups;
        },
    },
    watch: {
        waste_groups() {
            this.wasteGroupOptions = this.cloneObj(this.waste_groups);
        },
    },
};

const localizationSettingsMixin = {
    data() {
        return {
            localization: {},
            default_days: {
                monday: [{ open: '', close: '', id: 'monday_id', isOpen: false }],
                tuesday: [{ open: '', close: '', id: 'tuesday_id', isOpen: false }],
                wednesday: [{ open: '', close: '', id: 'wednesday_id', isOpen: false }],
                thursday: [{ open: '', close: '', id: 'thursday_id', isOpen: false }],
                friday: [{ open: '', close: '', id: 'friday_id', isOpen: false }],
                saturday: [{ open: '', close: '', id: 'saturday_id', isOpen: false }],
                sunday: [{ open: '', close: '', id: 'sunday_id', isOpen: false }],
            },
            days: {},
        };
    },
    methods: {
        setLocalization() {
            this.localization = {
                switchOpen: this.$__('Open'),
                switchClosed: this.$__('Closed'),
                placeholderOpens: this.$__('Opens'),
                placeholderCloses: this.$__('Closes'),
                addHours: this.$__('Add hours'),
                open: {
                    invalidInput: this.$__(
                        'Please enter an opening time in the 12 hour format (ie. 08:00 AM). You may also enter "24 hours".'
                    ),
                    greaterThanNext: this.$__('Please enter an opening time that is before the closing time.'),
                    lessThanPrevious: this.$__('Please enter an opening time that is after the previous closing time.'),
                    midnightNotLast: this.$__("Midnight can only be selected for the day's last closing time."),
                },
                close: {
                    invalidInput: this.$__(
                        'Please enter a closing time in the 12 hour format (ie. 05:00 PM). You may also enter "24 hours" or "Midnight".'
                    ),
                    greaterThanNext: this.$__('Please enter a closing time that is after the opening time.'),
                    lessThanPrevious: this.$__('Please enter a closing time that is before the next opening time.'),
                    midnightNotLast: this.$__("Midnight can only be selected for the day's last closing time."),
                },
                t24hours: this.$__('24 hours'),
                midnight: this.$__('Midnight'),
                days: {
                    // Nuanses
                    // '2400' > '0000', so can be set only for 'close'
                    // open: '0000' and close: '2400' same as '24hrs' on both
                    // same value in 'open' and 'close' denied except '24hrs'
                    // '2400' and 'midnight' is same
                    // if set any time to open or close or both, and set
                    // isOpen to false, then this day will be just closed
                    monday: this.$__('Monday'),
                    tuesday: this.$__('Tuesday'),
                    wednesday: this.$__('Wednesday'),
                    thursday: this.$__('Thursday'),
                    friday: this.$__('Friday'),
                    saturday: this.$__('Saturday'),
                    sunday: this.$__('Sunday'),
                    newYearsEve: this.$__('New Year\'s Eve'), // prettier-ignore
                    newYearsDay: this.$__('New Year\'s Day'), // prettier-ignore
                    martinLutherKingJrDay: this.$__('Martin Luther King, Jr. Day'),
                    presidentsDay: this.$__('Presidents\' Day'), // prettier-ignore
                    easter: this.$__('Easter'),
                    memorialDay: this.$__('Memorial Day'),
                    independenceDay: this.$__('Independence Day'),
                    fourthOfJuly: this.$__('4th of July'),
                    laborDay: this.$__('Labor Day'),
                    columbusDay: this.$__('Columbus Day'),
                    veteransDay: this.$__('Veterans Day'),
                    thanksgivingDay: this.$__('Thanksgiving Day'),
                    christmasEve: this.$__('Christmas Eve'),
                    christmas: this.$__('Christmas'),
                },
            };
        },
    },
    created() {
        this.setLocalization();
    },
    watch: {
        'lang.code': {
            handler() {
                this.setLocalization();
            },
        },
    },
};

const non_fluid_waste_mixin = {
    data() {
        return {
            physical_properties_options: [],
            packaging_types_options: [],
        };
    },
    methods: {
        fetchPhysicalProperties() {
            let self = this;
            if (self.physical_properties_options.length > 0) {
                return;
            }
            agent.PhysicalProperties.getPhysicalProperties().then(function (result) {
                self.physical_properties_options = result['data'];
            });
        },
        fetchPackagingTypes(addElseOption = false) {
            let self = this;
            if (self.packaging_types_options.length > 0) {
                return;
            }
            agent.PackagingTypes.getPackagingTypes().then(function (result) {
                self.packaging_types_options = result['data'];
                if (addElseOption) {
                    self.packaging_types_options.push({ id: 0, label: self.$__('Other') });
                }
            });
        },
    },
};

const numericValidator = {
    methods: {
        isValidNumber(value, maxDigits = 7, maxDecimals = null) {
            if (!value && value !== 0) return true; // Handle non-numeric and empty values
            const regex = /^[0-9,.-]+$/;
            if (!regex.test(value)) return false;
            // Convert to string and split at the decimal point
            const stringValue = value.toString();
            const parts = stringValue.split('.');
            // Check if the integer part exceeds maxDigits digits
            if (parts[0].length > maxDigits) return false;
            if (maxDecimals === null) return true;
            // Check if the fractional part exists and exceeds maxDecimals digits
            if (parts[1] && parts[1].length > maxDecimals) {
                parts[1] = parts[1].slice(0, maxDecimals);
            }
            return !(parts[1] && parts[1].length > maxDecimals);
        },
    },
};

const permissionMixin = {
    computed: {
        ...mapState({
            user: state => state.auth.user,
        }),
    },
    methods: {
        hasPermission(code) {
            const authUser =
                this.$store && this.$store.state && this.$store.state.auth ? this.$store.state.auth.user : this.user;
            const permissions = (authUser && authUser.permissions) || [];
            return permissions.includes(code) || (authUser && authUser.is_superuser);
        },
    },
};

export {
    utils,
    messages,
    trans_datatable,
    fetchers,
    event_parser,
    order_utils,
    master_table_component,
    appDatatableMixin,
    nested_objects_mixin,
    card_mixin,
    tab_mixin,
    watcher_mixin,
    get_params_mixin,
    array_mixin,
    switch_tabs_mixin,
    switch_tab_mixin,
    vmodel_tab_mixin,
    vmodel_card_mixin,
    bv_modal_utils,
    currency_mixin,
    coerce_props_mixin,
    string_mixin,
    b_modal_mixin,
    datatable_mixin,
    custom_table_sorting_mixin,
    SORT_ORDER,
    datatable_formatting_mixin,
    filter_remember_mixin,
    datepicker_extension_mixin,
    hash,
    modal_worker,
    roles_mixin,
    ultimateDragMixin,
    user_mixin,
    price_list_mixin,
    resource_socket,
    get_socket_mixin,
    shared_filters_mixin,
    getWasteGroupsMixin,
    localizationSettingsMixin,
    non_fluid_waste_mixin,
    numericValidator,
    permissionMixin,
};
